import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useAssetContext } from '@/context/AssetContext';
import { 
  logAIFinderSearch,
  logAIFinderResults,
  logAIFinderSelection,
  logAIFinderApply,
  logSearchSaved
} from '@/utils/activityLogger';
import { 
  ArrowLeft,
  ArrowRight,
  Sparkles,
  GraduationCap,
  DollarSign,
  Globe,
  Trophy,
  TrendingUp,
  PiggyBank,
  Target,
  BookOpen,
  Briefcase,
  MapPin,
  Loader2,
  Star,
  ExternalLink,
  CheckCircle2,
  Search,
  X,
  Calculator,
  Banknote,
  AlertCircle,
  Phone,
  Check,
  Send,
  FileText,
  AlertTriangle,
  Bookmark,
  BookmarkCheck,
  BookmarkPlus,
  Plane,
  Plus,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const steps = [
  { id: 1, title: 'Academic Background', icon: BookOpen },
  { id: 2, title: 'Test Scores', icon: Target },
  { id: 3, title: 'Preferences', icon: Globe },
  { id: 4, title: 'Budget & Goals', icon: DollarSign },
  { id: 5, title: 'Priorities', icon: Star }
];

const countries = ['USA', 'UK', 'Canada', 'Australia', 'Germany', 'New Zealand', 'Ireland', 'France', 'Netherlands', 'Dubai'];
const degrees = ['bachelors', 'masters', 'phd', 'diploma'];

// Degree types for selection (organized by level)
const degreeTypes = [
  // High School / 12th
  '12th / High School',
  '10+2',
  'Higher Secondary',
  'A-Levels',
  'IB Diploma',
  // Diploma
  'Diploma',
  'ITI',
  'Polytechnic',
  // Undergraduate
  'B.Tech',
  'B.E.',
  'B.Sc',
  'B.A.',
  'B.Com',
  'BBA',
  'BCA',
  'B.Arch',
  'B.Des',
  'B.Ed',
  'B.Pharm',
  'MBBS',
  'BDS',
  'LLB',
  'BA LLB',
  'BHM',
  'BJMC',
  'BFA',
  // Postgraduate
  'M.Tech',
  'M.E.',
  'M.Sc',
  'M.A.',
  'M.Com',
  'MBA',
  'MCA',
  'M.Arch',
  'M.Des',
  'M.Ed',
  'M.Pharm',
  'MD',
  'MS (Medical)',
  'LLM',
  'MFA',
  'MJMC',
  'MPH',
  // PG Diploma
  'PG Diploma',
  'PGDM',
  // Doctoral
  'Ph.D',
  'D.M.',
  'M.Ch.',
  // Other
  'Other'
];

// Official Study Areas list
const studyAreas = [
  'Accounting & Finance',
  'Agriculture & Food Science',
  'AI & Machine Learning',
  'Architecture',
  'Arts & Humanities',
  'Biology & Biomedical',
  'Built Environment',
  'Business & MBA',
  'Chemistry',
  'Computer Science',
  'Creative Arts & Design',
  'Criminology & Justice',
  'Cyber Security',
  'Data Science',
  'Dentistry',
  'Economics',
  'Education & Teaching',
  'Engineering Civil',
  'Engineering Mechanical',
  'Engineering Electrical',
  'Environmental Science',
  'Film & Television',
  'Health Science',
  'Hospitality & Tourism',
  'Information Technology',
  'Journalism',
  'Law & Legal Studies',
  'Marketing',
  'Mathematics & Statistics',
  'Media & Communication',
  'Medicine & Medical Science',
  'Music & Performing Arts',
  'Nursing',
  'Pharmacy',
  'Physics',
  'Political Science',
  'Project Management',
  'Psychology',
  'Robotics',
  'Social Work',
  'Sports & Exercise Science',
  'Supply Chain & Logistics',
  'Veterinary Science'
];

// Study areas with their related fields of interest (expanded mappings)
const studyAreaMappings = {
  'Accounting & Finance': ['Accounting & Finance', 'Business & MBA', 'Economics', 'Mathematics & Statistics', 'Law & Legal Studies', 'Information Technology', 'Project Management'],
  'Agriculture & Food Science': ['Agriculture & Food Science', 'Biology & Biomedical', 'Environmental Science', 'Chemistry', 'Veterinary Science', 'Business & MBA', 'Economics'],
  'AI & Machine Learning': ['AI & Machine Learning', 'Computer Science', 'Data Science', 'Robotics', 'Mathematics & Statistics', 'Engineering Electrical', 'Information Technology', 'Cyber Security'],
  'Architecture': ['Architecture', 'Built Environment', 'Creative Arts & Design', 'Engineering Civil', 'Environmental Science', 'Project Management', 'Arts & Humanities'],
  'Arts & Humanities': ['Arts & Humanities', 'Creative Arts & Design', 'Media & Communication', 'Music & Performing Arts', 'Film & Television', 'Education & Teaching', 'Journalism', 'Psychology'],
  'Biology & Biomedical': ['Biology & Biomedical', 'Medicine & Medical Science', 'Health Science', 'Chemistry', 'Pharmacy', 'Environmental Science', 'Veterinary Science', 'Psychology'],
  'Built Environment': ['Built Environment', 'Architecture', 'Engineering Civil', 'Environmental Science', 'Project Management', 'Creative Arts & Design', 'Economics'],
  'Business & MBA': ['Business & MBA', 'Accounting & Finance', 'Marketing', 'Project Management', 'Economics', 'Supply Chain & Logistics', 'Information Technology', 'Law & Legal Studies', 'Psychology'],
  'Chemistry': ['Chemistry', 'Biology & Biomedical', 'Pharmacy', 'Environmental Science', 'Medicine & Medical Science', 'Agriculture & Food Science', 'Engineering Mechanical'],
  'Computer Science': ['Computer Science', 'AI & Machine Learning', 'Data Science', 'Cyber Security', 'Information Technology', 'Robotics', 'Mathematics & Statistics', 'Engineering Electrical'],
  'Creative Arts & Design': ['Creative Arts & Design', 'Architecture', 'Film & Television', 'Media & Communication', 'Marketing', 'Arts & Humanities', 'Music & Performing Arts', 'Psychology'],
  'Criminology & Justice': ['Criminology & Justice', 'Law & Legal Studies', 'Psychology', 'Social Work', 'Political Science', 'Journalism', 'Economics'],
  'Cyber Security': ['Cyber Security', 'Computer Science', 'Information Technology', 'Data Science', 'AI & Machine Learning', 'Law & Legal Studies', 'Business & MBA'],
  'Data Science': ['Data Science', 'AI & Machine Learning', 'Computer Science', 'Mathematics & Statistics', 'Business & MBA', 'Economics', 'Information Technology', 'Health Science'],
  'Dentistry': ['Dentistry', 'Medicine & Medical Science', 'Health Science', 'Biology & Biomedical', 'Chemistry', 'Psychology', 'Business & MBA'],
  'Economics': ['Economics', 'Accounting & Finance', 'Business & MBA', 'Political Science', 'Mathematics & Statistics', 'Law & Legal Studies', 'Data Science', 'Journalism'],
  'Education & Teaching': ['Education & Teaching', 'Psychology', 'Social Work', 'Arts & Humanities', 'Media & Communication', 'Music & Performing Arts', 'Sports & Exercise Science', 'Health Science'],
  'Engineering Civil': ['Engineering Civil', 'Built Environment', 'Architecture', 'Environmental Science', 'Project Management', 'Mathematics & Statistics', 'Engineering Mechanical'],
  'Engineering Mechanical': ['Engineering Mechanical', 'Robotics', 'Physics', 'Engineering Electrical', 'Mathematics & Statistics', 'AI & Machine Learning', 'Environmental Science', 'Project Management'],
  'Engineering Electrical': ['Engineering Electrical', 'Engineering Mechanical', 'Computer Science', 'Robotics', 'Physics', 'AI & Machine Learning', 'Mathematics & Statistics', 'Information Technology'],
  'Environmental Science': ['Environmental Science', 'Agriculture & Food Science', 'Biology & Biomedical', 'Chemistry', 'Engineering Civil', 'Political Science', 'Economics', 'Built Environment'],
  'Film & Television': ['Film & Television', 'Media & Communication', 'Creative Arts & Design', 'Journalism', 'Music & Performing Arts', 'Arts & Humanities', 'Marketing', 'Psychology'],
  'Health Science': ['Health Science', 'Nursing', 'Medicine & Medical Science', 'Psychology', 'Biology & Biomedical', 'Sports & Exercise Science', 'Social Work', 'Pharmacy'],
  'Hospitality & Tourism': ['Hospitality & Tourism', 'Business & MBA', 'Marketing', 'Project Management', 'Economics', 'Media & Communication', 'Supply Chain & Logistics', 'Arts & Humanities'],
  'Information Technology': ['Information Technology', 'Computer Science', 'Cyber Security', 'Data Science', 'AI & Machine Learning', 'Business & MBA', 'Project Management', 'Engineering Electrical'],
  'Journalism': ['Journalism', 'Media & Communication', 'Film & Television', 'Political Science', 'Arts & Humanities', 'Marketing', 'Law & Legal Studies', 'Economics'],
  'Law & Legal Studies': ['Law & Legal Studies', 'Criminology & Justice', 'Political Science', 'Economics', 'Business & MBA', 'Journalism', 'Psychology', 'Social Work'],
  'Marketing': ['Marketing', 'Business & MBA', 'Media & Communication', 'Psychology', 'Data Science', 'Creative Arts & Design', 'Economics', 'Information Technology'],
  'Mathematics & Statistics': ['Mathematics & Statistics', 'Data Science', 'Physics', 'Economics', 'Computer Science', 'AI & Machine Learning', 'Accounting & Finance', 'Engineering Mechanical'],
  'Media & Communication': ['Media & Communication', 'Journalism', 'Film & Television', 'Marketing', 'Creative Arts & Design', 'Psychology', 'Political Science', 'Arts & Humanities'],
  'Medicine & Medical Science': ['Medicine & Medical Science', 'Biology & Biomedical', 'Health Science', 'Pharmacy', 'Chemistry', 'Psychology', 'Dentistry', 'Nursing'],
  'Music & Performing Arts': ['Music & Performing Arts', 'Arts & Humanities', 'Creative Arts & Design', 'Film & Television', 'Education & Teaching', 'Media & Communication', 'Psychology'],
  'Nursing': ['Nursing', 'Health Science', 'Medicine & Medical Science', 'Psychology', 'Biology & Biomedical', 'Social Work', 'Education & Teaching'],
  'Pharmacy': ['Pharmacy', 'Chemistry', 'Medicine & Medical Science', 'Biology & Biomedical', 'Health Science', 'Business & MBA', 'Environmental Science'],
  'Physics': ['Physics', 'Mathematics & Statistics', 'Engineering Electrical', 'Engineering Mechanical', 'Computer Science', 'Data Science', 'Environmental Science', 'Robotics'],
  'Political Science': ['Political Science', 'Law & Legal Studies', 'Economics', 'Journalism', 'Media & Communication', 'Arts & Humanities', 'Social Work', 'Criminology & Justice'],
  'Project Management': ['Project Management', 'Business & MBA', 'Engineering Civil', 'Information Technology', 'Supply Chain & Logistics', 'Economics', 'Marketing', 'Architecture'],
  'Psychology': ['Psychology', 'Health Science', 'Education & Teaching', 'Social Work', 'Criminology & Justice', 'Marketing', 'Nursing', 'Medicine & Medical Science'],
  'Robotics': ['Robotics', 'AI & Machine Learning', 'Engineering Mechanical', 'Engineering Electrical', 'Computer Science', 'Physics', 'Mathematics & Statistics', 'Data Science'],
  'Social Work': ['Social Work', 'Psychology', 'Criminology & Justice', 'Education & Teaching', 'Health Science', 'Law & Legal Studies', 'Political Science', 'Nursing'],
  'Sports & Exercise Science': ['Sports & Exercise Science', 'Health Science', 'Psychology', 'Biology & Biomedical', 'Nursing', 'Education & Teaching', 'Business & MBA', 'Hospitality & Tourism'],
  'Supply Chain & Logistics': ['Supply Chain & Logistics', 'Business & MBA', 'Project Management', 'Economics', 'Information Technology', 'Data Science', 'Engineering Mechanical'],
  'Veterinary Science': ['Veterinary Science', 'Biology & Biomedical', 'Agriculture & Food Science', 'Health Science', 'Chemistry', 'Environmental Science', 'Business & MBA'],
};

// Expanded list of fields of interest (same as study areas for consistency)
const allFields = [...studyAreas];

// Mapping of degree types to relevant course specializations
const degreeSpecializationsMap = {
  // High School / 12th - General streams
  '12th / High School': ['Science - PCM (Physics, Chemistry, Math)', 'Science - PCB (Physics, Chemistry, Biology)', 'Science - PCMB (All four)', 'Commerce with Math', 'Commerce without Math', 'Arts / Humanities', 'Vocational / Technical'],
  '10+2': ['Science - PCM (Physics, Chemistry, Math)', 'Science - PCB (Physics, Chemistry, Biology)', 'Commerce with Math', 'Commerce without Math', 'Arts / Humanities'],
  'Higher Secondary': ['Science Stream', 'Commerce Stream', 'Arts Stream', 'Vocational Stream'],
  'A-Levels': ['Science Focus (Math, Physics, Chemistry)', 'Engineering Focus (Math, Physics, Computer Science)', 'Medical/Bio Focus (Biology, Chemistry)', 'Business Focus (Economics, Business Studies, Math)', 'Humanities Focus (English, History, Psychology)', 'Arts Focus (Art, Design, Media)'],
  'IB Diploma': ['Engineering Focus (Math HL, Physics HL, Computer Science)', 'Science Focus (Biology HL, Chemistry HL, Physics)', 'Medical/Pre-Med Focus (Biology HL, Chemistry HL)', 'Business & Economics Focus (Economics HL, Business Management)', 'Humanities Focus (History HL, English HL, Psychology)', 'Arts & Design Focus (Visual Arts, Film, Theatre)', 'Environmental Focus (ESS, Biology, Geography)', 'Social Sciences Focus (Psychology, Economics, Geography)'],
  
  // Diploma
  'Diploma': ['Computer Science', 'Mechanical Engineering', 'Civil Engineering', 'Electrical Engineering', 'Electronics', 'Information Technology', 'Business Administration', 'Hotel Management'],
  'ITI': ['Electrician', 'Fitter', 'Turner', 'Machinist', 'Welder', 'Plumber', 'Electronics Mechanic', 'Computer Operator', 'Draughtsman', 'Mechanic Motor Vehicle'],
  'Polytechnic': ['Computer Engineering', 'Mechanical Engineering', 'Civil Engineering', 'Electrical Engineering', 'Electronics Engineering', 'Automobile Engineering', 'Chemical Engineering'],
  
  // B.Tech / Engineering
  'B.Tech': ['Computer Science', 'Information Technology', 'Electronics & Communication', 'Electrical Engineering', 'Mechanical Engineering', 'Civil Engineering', 'Chemical Engineering', 'Aerospace Engineering', 'Biotechnology', 'Data Science', 'Artificial Intelligence', 'Robotics', 'Automobile Engineering'],
  'B.E.': ['Computer Science', 'Information Technology', 'Electronics & Communication', 'Electrical Engineering', 'Mechanical Engineering', 'Civil Engineering', 'Chemical Engineering', 'Production Engineering', 'Instrumentation'],
  
  // Science
  'B.Sc': ['Computer Science', 'Physics', 'Chemistry', 'Mathematics', 'Biology', 'Biotechnology', 'Microbiology', 'Zoology', 'Botany', 'Statistics', 'Data Science', 'Forensic Science', 'Agriculture', 'Nursing'],
  'M.Sc': ['Computer Science', 'Physics', 'Chemistry', 'Mathematics', 'Biology', 'Biotechnology', 'Microbiology', 'Data Science', 'Artificial Intelligence', 'Environmental Science', 'Forensic Science'],
  
  // Arts
  'B.A.': ['English', 'History', 'Political Science', 'Economics', 'Psychology', 'Sociology', 'Philosophy', 'Geography', 'Hindi', 'Sanskrit', 'Journalism', 'Public Administration'],
  'M.A.': ['English', 'History', 'Political Science', 'Economics', 'Psychology', 'Sociology', 'Philosophy', 'Public Administration', 'International Relations', 'Development Studies'],
  
  // Commerce
  'B.Com': ['Accounting', 'Finance', 'Taxation', 'Banking', 'Economics', 'Business Studies', 'Computer Applications', 'Honors'],
  'M.Com': ['Accounting', 'Finance', 'Taxation', 'Banking', 'Business Economics', 'International Business'],
  
  // Management
  'BBA': ['Marketing', 'Finance', 'Human Resources', 'Operations', 'International Business', 'Entrepreneurship', 'Business Analytics', 'Healthcare Management'],
  'MBA': ['Marketing', 'Finance', 'Human Resources', 'Operations', 'International Business', 'Entrepreneurship', 'Business Analytics', 'Healthcare Management', 'Supply Chain', 'Digital Marketing', 'Consulting', 'Strategy', 'Product Management'],
  'PGDM': ['Marketing', 'Finance', 'Human Resources', 'Operations', 'Business Analytics', 'International Business', 'Healthcare Management'],
  
  // Computer Applications
  'BCA': ['Programming', 'Web Development', 'Data Science', 'Networking', 'Database Management', 'Software Development', 'Cyber Security'],
  'MCA': ['Software Engineering', 'Data Science', 'Artificial Intelligence', 'Cloud Computing', 'Cyber Security', 'Systems Management', 'Web Technologies'],
  
  // Architecture & Design
  'B.Arch': ['Architecture', 'Urban Planning', 'Landscape Architecture', 'Interior Design', 'Sustainable Design'],
  'M.Arch': ['Urban Design', 'Sustainable Architecture', 'Landscape Architecture', 'Architectural Conservation', 'Housing'],
  'B.Des': ['Product Design', 'Communication Design', 'Fashion Design', 'Interior Design', 'UX/UI Design', 'Animation', 'Game Design', 'Textile Design'],
  'M.Des': ['Product Design', 'Communication Design', 'Interaction Design', 'Strategic Design', 'Design Management'],
  
  // Education
  'B.Ed': ['Science Education', 'Arts Education', 'Commerce Education', 'Primary Education', 'Special Education', 'Physical Education'],
  'M.Ed': ['Curriculum Development', 'Educational Psychology', 'Educational Administration', 'Special Education', 'Teacher Education'],
  
  // Pharmacy
  'B.Pharm': ['Pharmaceutical Chemistry', 'Pharmacology', 'Pharmaceutics', 'Pharmacognosy', 'Clinical Pharmacy'],
  'M.Pharm': ['Pharmaceutical Chemistry', 'Pharmacology', 'Pharmaceutics', 'Pharmacognosy', 'Pharmacy Practice', 'Regulatory Affairs'],
  
  // Medical
  'MBBS': ['General Medicine', 'Surgery', 'Pediatrics', 'Obstetrics & Gynecology', 'Orthopedics', 'Dermatology', 'Psychiatry', 'Radiology', 'Anesthesiology'],
  'BDS': ['Oral Surgery', 'Orthodontics', 'Prosthodontics', 'Periodontics', 'Endodontics', 'Public Health Dentistry'],
  'MD': ['Internal Medicine', 'Pediatrics', 'Dermatology', 'Psychiatry', 'Radiology', 'Anesthesiology', 'Pathology', 'Pharmacology'],
  'MS (Medical)': ['General Surgery', 'Orthopedics', 'Obstetrics & Gynecology', 'ENT', 'Ophthalmology', 'Neurosurgery'],
  'Nursing': ['Medical-Surgical Nursing', 'Pediatric Nursing', 'Psychiatric Nursing', 'Community Health Nursing', 'Critical Care Nursing'],
  
  // Law
  'LLB': ['Civil Law', 'Criminal Law', 'Corporate Law', 'Constitutional Law', 'Family Law', 'Intellectual Property', 'International Law'],
  'BA LLB': ['Constitutional Law', 'Corporate Law', 'Criminal Law', 'Civil Law', 'International Law', 'Human Rights Law'],
  'LLM': ['Corporate Law', 'International Law', 'Constitutional Law', 'Criminal Law', 'Intellectual Property', 'Cyber Law', 'Human Rights Law'],
  
  // Media & Communication
  'BJMC': ['Print Journalism', 'Broadcast Journalism', 'Digital Media', 'Advertising', 'Public Relations', 'Film Studies'],
  'MJMC': ['Journalism', 'Advertising', 'Public Relations', 'Digital Media', 'Corporate Communication', 'Media Management'],
  
  // Hospitality
  'BHM': ['Hotel Management', 'Food & Beverage', 'Front Office', 'Housekeeping', 'Event Management', 'Travel & Tourism'],
  
  // Fine Arts
  'BFA': ['Painting', 'Sculpture', 'Applied Arts', 'Photography', 'Ceramics', 'Printmaking', 'Animation'],
  'MFA': ['Painting', 'Sculpture', 'Applied Arts', 'Art History', 'Visual Communication'],
  
  // Public Health
  'MPH': ['Epidemiology', 'Biostatistics', 'Health Policy', 'Environmental Health', 'Global Health', 'Health Management'],
  
  // Postgraduate Engineering
  'M.Tech': ['Computer Science', 'Data Science', 'Artificial Intelligence', 'Machine Learning', 'Cyber Security', 'VLSI Design', 'Embedded Systems', 'Structural Engineering', 'Power Systems', 'Thermal Engineering'],
  'M.E.': ['Computer Science', 'Electronics', 'Mechanical', 'Civil', 'Electrical', 'Production Engineering'],
  
  // PG Diploma
  'PG Diploma': ['Data Science', 'Business Analytics', 'Digital Marketing', 'Human Resources', 'Finance', 'Project Management', 'Clinical Research'],
  
  // Doctoral
  'Ph.D': ['Computer Science', 'Physics', 'Chemistry', 'Biology', 'Mathematics', 'Engineering', 'Management', 'Economics', 'Psychology', 'Literature', 'Education'],
  'D.M.': ['Cardiology', 'Neurology', 'Gastroenterology', 'Nephrology', 'Oncology', 'Pulmonology', 'Endocrinology'],
  'M.Ch.': ['Cardiothoracic Surgery', 'Neurosurgery', 'Plastic Surgery', 'Urology', 'Pediatric Surgery', 'Surgical Oncology'],
  
  // Default/Other
  'Other': ['Computer Science', 'Business Administration', 'Engineering', 'Arts & Humanities', 'Science', 'Commerce', 'Medicine', 'Law']
};

// Mapping of current degree to suggested target degree level
const targetDegreeSuggestionMap = {
  // High School / Pre-University → Bachelors
  '12th / High School': 'bachelors',
  '10+2': 'bachelors',
  'Higher Secondary': 'bachelors',
  'A-Levels': 'bachelors',
  'IB Diploma': 'bachelors',
  
  // Diploma → Bachelors or Diploma (depending on pathway)
  'Diploma': 'bachelors',
  'ITI': 'diploma',
  'Polytechnic': 'bachelors',
  
  // Undergraduate → Masters
  'B.Tech': 'masters',
  'B.E.': 'masters',
  'B.Sc': 'masters',
  'B.A.': 'masters',
  'B.Com': 'masters',
  'BBA': 'masters',
  'BCA': 'masters',
  'B.Arch': 'masters',
  'B.Des': 'masters',
  'B.Ed': 'masters',
  'B.Pharm': 'masters',
  'MBBS': 'masters', // MD/MS pathway
  'BDS': 'masters',
  'LLB': 'masters',
  'BA LLB': 'masters',
  'BHM': 'masters',
  'BJMC': 'masters',
  'BFA': 'masters',
  
  // Postgraduate → PhD
  'M.Tech': 'phd',
  'M.E.': 'phd',
  'M.Sc': 'phd',
  'M.A.': 'phd',
  'M.Com': 'phd',
  'MBA': 'phd',
  'MCA': 'phd',
  'M.Arch': 'phd',
  'M.Des': 'phd',
  'M.Ed': 'phd',
  'M.Pharm': 'phd',
  'MD': 'phd',
  'MS (Medical)': 'phd',
  'LLM': 'phd',
  'MFA': 'phd',
  'MJMC': 'phd',
  'MPH': 'phd',
  
  // PG Diploma → Masters (upgrade pathway)
  'PG Diploma': 'masters',
  'PGDM': 'masters',
  
  // Doctoral → Postdoc/Research (default to masters for simplicity)
  'Ph.D': 'masters',
  'D.M.': 'masters',
  'M.Ch.': 'masters',
  
  // Default
  'Other': 'masters'
};

// Function to get suggested target degree based on current degree
const getSuggestedTargetDegree = (currentDegree) => {
  if (!currentDegree) return 'masters'; // Default
  return targetDegreeSuggestionMap[currentDegree] || 'masters';
};

// Field mapping based on current degree keywords - maps degree keywords to relevant fields
const fieldMappings = {
  // Computer & IT
  'computer': ['Computer Science', 'Data Science', 'AI & Machine Learning', 'Information Technology', 'Cyber Security'],
  'software': ['Computer Science', 'Information Technology', 'Data Science', 'AI & Machine Learning'],
  'information technology': ['Information Technology', 'Computer Science', 'Cyber Security', 'Data Science'],
  'it': ['Information Technology', 'Computer Science', 'Cyber Security'],
  'data': ['Data Science', 'AI & Machine Learning', 'Computer Science', 'Mathematics & Statistics'],
  'ai': ['AI & Machine Learning', 'Data Science', 'Computer Science', 'Robotics'],
  'machine learning': ['AI & Machine Learning', 'Data Science', 'Computer Science', 'Robotics'],
  'cyber': ['Cyber Security', 'Information Technology', 'Computer Science'],
  'robotics': ['Robotics', 'AI & Machine Learning', 'Engineering Mechanical', 'Engineering Electrical'],
  
  // Engineering
  'mechanical': ['Engineering Mechanical', 'Robotics', 'Physics', 'Engineering Electrical'],
  'civil': ['Engineering Civil', 'Built Environment', 'Architecture', 'Environmental Science'],
  'electrical': ['Engineering Electrical', 'Engineering Mechanical', 'Computer Science', 'Robotics'],
  'electronics': ['Engineering Electrical', 'Computer Science', 'Robotics'],
  'engineering': ['Engineering Mechanical', 'Engineering Civil', 'Engineering Electrical'],
  
  // Architecture & Design
  'architecture': ['Architecture', 'Built Environment', 'Creative Arts & Design', 'Engineering Civil'],
  'interior': ['Architecture', 'Creative Arts & Design', 'Built Environment'],
  'design': ['Creative Arts & Design', 'Architecture', 'Media & Communication'],
  'graphic': ['Creative Arts & Design', 'Media & Communication', 'Film & Television'],
  'art': ['Creative Arts & Design', 'Arts & Humanities', 'Music & Performing Arts'],
  'film': ['Film & Television', 'Media & Communication', 'Creative Arts & Design'],
  'media': ['Media & Communication', 'Film & Television', 'Journalism', 'Marketing'],
  
  // Business
  'business': ['Business & MBA', 'Accounting & Finance', 'Marketing', 'Economics', 'Project Management'],
  'commerce': ['Business & MBA', 'Accounting & Finance', 'Economics'],
  'bcom': ['Business & MBA', 'Accounting & Finance', 'Economics'],
  'bba': ['Business & MBA', 'Accounting & Finance', 'Marketing'],
  'mba': ['Business & MBA', 'Accounting & Finance', 'Marketing', 'Project Management'],
  'finance': ['Accounting & Finance', 'Business & MBA', 'Economics'],
  'accounting': ['Accounting & Finance', 'Business & MBA', 'Economics'],
  'economics': ['Economics', 'Accounting & Finance', 'Business & MBA', 'Political Science'],
  'marketing': ['Marketing', 'Business & MBA', 'Media & Communication'],
  'management': ['Business & MBA', 'Project Management', 'Supply Chain & Logistics'],
  'supply chain': ['Supply Chain & Logistics', 'Business & MBA', 'Project Management'],
  'logistics': ['Supply Chain & Logistics', 'Business & MBA', 'Project Management'],
  'project': ['Project Management', 'Business & MBA', 'Engineering Civil'],
  
  // Science
  'biology': ['Biology & Biomedical', 'Medicine & Medical Science', 'Health Science', 'Environmental Science'],
  'bio': ['Biology & Biomedical', 'Medicine & Medical Science', 'Health Science'],
  'biotech': ['Biology & Biomedical', 'Medicine & Medical Science', 'Pharmacy'],
  'chemistry': ['Chemistry', 'Pharmacy', 'Biology & Biomedical', 'Environmental Science'],
  'physics': ['Physics', 'Mathematics & Statistics', 'Engineering Electrical', 'Engineering Mechanical'],
  'mathematics': ['Mathematics & Statistics', 'Data Science', 'Computer Science', 'Physics'],
  'math': ['Mathematics & Statistics', 'Data Science', 'Computer Science'],
  'statistics': ['Mathematics & Statistics', 'Data Science', 'Economics'],
  'environment': ['Environmental Science', 'Agriculture & Food Science', 'Biology & Biomedical'],
  'agriculture': ['Agriculture & Food Science', 'Environmental Science', 'Biology & Biomedical'],
  'veterinary': ['Veterinary Science', 'Biology & Biomedical', 'Agriculture & Food Science'],
  
  // Healthcare & Medicine
  'medicine': ['Medicine & Medical Science', 'Health Science', 'Biology & Biomedical', 'Pharmacy'],
  'mbbs': ['Medicine & Medical Science', 'Health Science', 'Biology & Biomedical'],
  'medical': ['Medicine & Medical Science', 'Health Science', 'Biology & Biomedical'],
  'nursing': ['Nursing', 'Health Science', 'Medicine & Medical Science'],
  'pharmacy': ['Pharmacy', 'Chemistry', 'Medicine & Medical Science'],
  'dental': ['Dentistry', 'Medicine & Medical Science', 'Health Science'],
  'dentistry': ['Dentistry', 'Medicine & Medical Science', 'Health Science'],
  'health': ['Health Science', 'Nursing', 'Medicine & Medical Science', 'Psychology'],
  'sports': ['Sports & Exercise Science', 'Health Science', 'Psychology'],
  
  // Social Sciences
  'psychology': ['Psychology', 'Health Science', 'Education & Teaching', 'Social Work'],
  'sociology': ['Social Work', 'Psychology', 'Political Science', 'Criminology & Justice'],
  'political': ['Political Science', 'Law & Legal Studies', 'Economics', 'Journalism'],
  'criminology': ['Criminology & Justice', 'Law & Legal Studies', 'Psychology', 'Social Work'],
  'social work': ['Social Work', 'Psychology', 'Criminology & Justice', 'Education & Teaching'],
  
  // Law
  'law': ['Law & Legal Studies', 'Political Science', 'Criminology & Justice'],
  'llb': ['Law & Legal Studies', 'Political Science'],
  'legal': ['Law & Legal Studies', 'Political Science', 'Criminology & Justice'],
  
  // Communications & Journalism
  'journalism': ['Journalism', 'Media & Communication', 'Film & Television', 'Political Science'],
  'communication': ['Media & Communication', 'Journalism', 'Marketing', 'Film & Television'],
  'mass comm': ['Media & Communication', 'Journalism', 'Film & Television'],
  
  // Education
  'education': ['Education & Teaching', 'Psychology', 'Social Work'],
  'teaching': ['Education & Teaching', 'Psychology'],
  'bed': ['Education & Teaching', 'Psychology'],
  
  // Hospitality
  'hotel': ['Hospitality & Tourism', 'Business & MBA', 'Marketing'],
  'hospitality': ['Hospitality & Tourism', 'Business & MBA', 'Marketing'],
  'tourism': ['Hospitality & Tourism', 'Business & MBA', 'Marketing'],
  
  // Arts & Humanities
  'music': ['Music & Performing Arts', 'Arts & Humanities', 'Creative Arts & Design'],
  'performing': ['Music & Performing Arts', 'Arts & Humanities', 'Film & Television'],
  'humanities': ['Arts & Humanities', 'Education & Teaching', 'Media & Communication'],
};

// Function to get suggested fields based on selected study area (primary) or course name/degree text (fallback)
const getSuggestedFields = (studyArea, courseName = '', degreeText = '') => {
  // First priority: use the selected study area mapping
  if (studyArea && studyAreaMappings[studyArea]) {
    const suggested = studyAreaMappings[studyArea];
    const other = allFields.filter(f => !suggested.includes(f));
    return { suggested, other, source: studyArea };
  }
  
  // Second priority: parse from course name
  const textToSearch = courseName || degreeText;
  if (!textToSearch) return { suggested: [], other: allFields, source: null };
  
  const lowerText = textToSearch.toLowerCase();
  let suggestedSet = new Set();
  
  // Check for keyword matches - prioritize longer matches first
  const sortedKeywords = Object.keys(fieldMappings).sort((a, b) => b.length - a.length);
  
  sortedKeywords.forEach(keyword => {
    // Use word boundary matching for short keywords (3 chars or less)
    if (keyword.length <= 3) {
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      if (regex.test(lowerText)) {
        fieldMappings[keyword].forEach(f => suggestedSet.add(f));
      }
    } else {
      if (lowerText.includes(keyword)) {
        fieldMappings[keyword].forEach(f => suggestedSet.add(f));
      }
    }
  });
  
  // If no matches found, return all fields without suggestions
  if (suggestedSet.size === 0) {
    return { suggested: [], other: allFields, source: null };
  }
  
  const suggested = Array.from(suggestedSet);
  const other = allFields.filter(f => !suggestedSet.has(f));
  
  return { suggested, other, source: courseName || textToSearch };
};

const AIUniversityFinder = () => {
  const { isDark } = useTheme();
  const { user, isAuthenticated, loading: authLoading } = useStudentAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { getLogo } = useAssetContext();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Currency symbols mapping
  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'USD': '$', 'GBP': '£', 'EUR': '€', 'CAD': 'C$', 'AUD': 'A$',
      'NZD': 'NZ$', 'SGD': 'S$', 'INR': '₹', 'JPY': '¥', 'CNY': '¥',
      'CHF': 'CHF', 'SEK': 'kr', 'DKK': 'kr', 'NOK': 'kr', 'KRW': '₩'
    };
    return symbols[currencyCode] || '$';
  };
  
  // Get currency code by country (fallback when AI doesn't provide it)
  const getCurrencyByCountry = (country) => {
    const countryToCurrency = {
      'United Kingdom': 'GBP', 'UK': 'GBP',
      'Canada': 'CAD',
      'Australia': 'AUD',
      'Germany': 'EUR', 'France': 'EUR', 'Netherlands': 'EUR', 'Ireland': 'EUR', 'Italy': 'EUR', 'Spain': 'EUR',
      'New Zealand': 'NZD',
      'Singapore': 'SGD',
      'USA': 'USD', 'United States': 'USD',
      'India': 'INR',
      'Japan': 'JPY',
      'China': 'CNY',
      'Switzerland': 'CHF',
      'Sweden': 'SEK', 'Denmark': 'DKK', 'Norway': 'NOK',
      'South Korea': 'KRW'
    };
    return countryToCurrency[country] || 'USD';
  };
  
  // Format tuition - use local currency if available, otherwise convert from USD
  const formatTuition = (uni) => {
    // If AI provided local currency, use it
    if (uni.tuition_local && uni.tuition_currency) {
      const symbol = getCurrencySymbol(uni.tuition_currency);
      return `${symbol}${uni.tuition_local.toLocaleString()}/yr`;
    }
    
    // Fallback: Convert USD to local currency based on country
    if (uni.tuition_usd && uni.country) {
      const currency = getCurrencyByCountry(uni.country);
      const symbol = getCurrencySymbol(currency);
      
      // Approximate conversion rates from USD
      const rates = {
        'GBP': 0.79, 'EUR': 0.92, 'CAD': 1.36, 'AUD': 1.53, 'NZD': 1.67,
        'SGD': 1.34, 'INR': 83.5, 'JPY': 149, 'CNY': 7.24, 'CHF': 0.88,
        'SEK': 10.5, 'DKK': 6.9, 'NOK': 10.7, 'KRW': 1320, 'USD': 1
      };
      const rate = rates[currency] || 1;
      const localAmount = Math.round(uni.tuition_usd * rate);
      return `${symbol}${localAmount.toLocaleString()}/yr`;
    }
    
    // Last resort: show USD as-is
    if (uni.tuition_usd) {
      return `$${uni.tuition_usd.toLocaleString()}/yr`;
    }
    
    return null;
  };
  
  // Helper function to scroll to top when changing steps (mobile-friendly)
  const scrollToTop = () => {
    // Force immediate scroll to top (most reliable for mobile)
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // For Safari
    
    // Also try scrolling the main content area if it exists
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
    
    // Scroll to the top anchor element
    const topElement = document.getElementById('ai-finder-top');
    if (topElement) {
      topElement.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    
    // Fallback: Use setTimeout to ensure scroll happens after React render
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
  };
  
  // Navigation helper that scrolls to top
  const goToStep = (step) => {
    if (typeof step === 'function') {
      setCurrentStep(step);
    } else {
      setCurrentStep(step);
    }
    scrollToTop();
  };
  
  // Auto-scroll to top whenever currentStep changes (safety net)
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);
  
  const [results, setResults] = useState(null);
  const [fieldSearch, setFieldSearch] = useState('');
  const [studyAreaSearch, setStudyAreaSearch] = useState('');
  const [showStudyAreaDropdown, setShowStudyAreaDropdown] = useState(false);
  const [degreeTypeSearch, setDegreeTypeSearch] = useState('');
  const [showDegreeTypeDropdown, setShowDegreeTypeDropdown] = useState(false);
  const [specializationSearch, setSpecializationSearch] = useState('');
  const [showSpecializationDropdown, setShowSpecializationDropdown] = useState(false);
  const [budgetCurrency, setBudgetCurrency] = useState('USD');
  const studyAreaRef = useRef(null);
  const degreeTypeRef = useRef(null);
  const specializationRef = useRef(null);
  
  // Search limits state
  const [searchStatus, setSearchStatus] = useState(null);
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Loading messages for engagement
  const loadingMessages = [
    "Analyzing your academic profile...",
    "Matching your preferences with universities...",
    "Calculating admission probabilities...",
    "Finding the best programs for you...",
    "Comparing tuition fees and scholarships...",
    "Almost there! Finalizing recommendations..."
  ];
  
  // Selection and Apply state
  const [selectedUniversities, setSelectedUniversities] = useState(new Set());
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [applying, setApplying] = useState(false);
  const [userDocuments, setUserDocuments] = useState([]);
  
  // Saved universities state
  const [savedUniversities, setSavedUniversities] = useState(new Set());
  const [savingUniversity, setSavingUniversity] = useState(null);
  
  // Search session state for saving
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [searchSaved, setSearchSaved] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  
  // Suggested universities state (shown after Step 1 completion)
  const [suggestedUniversities, setSuggestedUniversities] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const [profile, setProfile] = useState({
    current_degree: '',
    current_study_area: '', // New field for selected study area
    selected_fields: [], // Fields of study selected based on study area
    field_of_study: '',
    gpa: '',
    gpa_scale: 10,
    original_percentage: null, // Original percentage from GPA calculator for cross-country conversion
    gpa_calculated_for: null, // Which country the GPA was calculated for (e.g., 'Australia')
    gre_score: '',
    gmat_score: '',
    ielts_score: '',
    toefl_score: '',
    pte_score: '',
    sat_score: '',
    target_degree: 'masters',
    preferred_countries: [],
    preferred_majors: [],
    budget_min: 20000,
    budget_max: 60000,
    needs_scholarship: false,
    career_goals: '',
    work_experience_years: 0,
    // Student Motivators (scale 1-5: Not Important → Critical)
    priority_course: 3,        // Course Oriented - Quality of specific program
    priority_ranking: 3,       // Reputation Oriented - University prestige/ranking
    priority_career: 3,        // Career Oriented - Job placement, industry connections
    priority_pr: 3,            // PR Opportunities - Immigration/residency pathways
    priority_cost: 3,          // Cost Focused - Affordability, ROI
    priority_location: 3,      // Location Oriented - City, lifestyle
    priority_culture: 3        // Cultural Experience - Campus life, diversity
  });
  
  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (studyAreaRef.current && !studyAreaRef.current.contains(event.target)) {
        setShowStudyAreaDropdown(false);
      }
      if (degreeTypeRef.current && !degreeTypeRef.current.contains(event.target)) {
        setShowDegreeTypeDropdown(false);
      }
      if (specializationRef.current && !specializationRef.current.contains(event.target)) {
        setShowSpecializationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if user came with a preset persona (from navigation state OR user profile)
  useEffect(() => {
    // Priority: location.state.persona > user.primary_motivation
    const persona = location.state?.persona || user?.primary_motivation;
    
    if (persona) {
      // Map both old and new persona IDs to priority configurations
      const personaPriorities = {
        // Legacy personas
        ranking_hunter: { priority_ranking: 5, priority_roi: 3, priority_cost: 2, priority_location: 2 },
        roi_seeker: { priority_ranking: 3, priority_roi: 5, priority_cost: 3, priority_location: 2 },
        smart_spender: { priority_ranking: 2, priority_roi: 3, priority_cost: 5, priority_location: 3 },
        global_citizen: { priority_ranking: 2, priority_roi: 2, priority_cost: 3, priority_location: 5 },
        
        // New Journey Selector personas
        'course-known': { 
          priority_ranking: 5,  // Course-focused students care about rankings
          priority_roi: 3, 
          priority_cost: 2, 
          priority_location: 2 
        },
        'budget-clarity': { 
          priority_ranking: 2, 
          priority_roi: 4, 
          priority_cost: 5,  // Budget is their top priority
          priority_location: 2 
        },
        'country-help': { 
          priority_ranking: 2, 
          priority_roi: 2, 
          priority_cost: 3, 
          priority_location: 5  // Location/destination is their focus
        },
        'career-focus': { 
          priority_ranking: 3, 
          priority_roi: 5,  // Career/ROI is their top priority
          priority_cost: 2, 
          priority_location: 3 
        },
        'ai-guided': { 
          priority_ranking: 3, 
          priority_roi: 3, 
          priority_cost: 3, 
          priority_location: 3  // Balanced for AI guidance
        }
      };
      
      if (personaPriorities[persona]) {
        setProfile(prev => ({ ...prev, ...personaPriorities[persona] }));
        console.log(`Applied persona priorities for: ${persona}`);
      }
    }
  }, [location.state, user?.primary_motivation]);

  // Load saved search if URL has ?load=search_id
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchId = params.get('load');
    
    if (searchId && user) {
      const loadSavedSearch = async () => {
        try {
          setLoading(true);
          const res = await fetch(`${window.location.origin}/api/ai/searches/${searchId}`, {
            credentials: 'include'
          });
          
          if (res.ok) {
            const data = await res.json();
            
            // Restore the profile
            if (data.profile) {
              setProfile(data.profile);
            }
            
            // Restore the results and go to results step
            if (data.results) {
              setResults(data.results);
              setCurrentStep(6);
              setSearchSaved(true);
              scrollToTop();
              toast.success(`Loaded: ${data.search_name}`);
            }
            
            // Clear the URL parameter
            window.history.replaceState({}, document.title, '/uac-desk/ai-finder');
          } else {
            toast.error('Could not load saved search');
          }
        } catch (error) {
          console.error('Error loading saved search:', error);
          toast.error('Failed to load saved search');
        } finally {
          setLoading(false);
        }
      };
      
      loadSavedSearch();
    }
  }, [location.search, user]);

  // Handle returning from documents page - refresh documents and restore application state
  useEffect(() => {
    if (location.state?.fromDocuments) {
      // Refresh documents when returning from documents page
      const refreshDocumentsAndRestoreState = async () => {
        if (!user?.user_id) return;
        try {
          const res = await fetch(`${window.location.origin}/api/counselor/profile/${user.user_id}`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            if (data.documents && typeof data.documents === 'object') {
              const docsArray = Object.entries(data.documents)
                .filter(([key, value]) => value && value.uploaded)
                .map(([key]) => key);
              setUserDocuments(docsArray);
              
              // Check if we have a pending application to restore
              const pendingAppStr = localStorage.getItem('pendingApplication');
              if (pendingAppStr) {
                try {
                  const pendingApp = JSON.parse(pendingAppStr);
                  // Only restore if it's recent (within 30 minutes)
                  if (Date.now() - pendingApp.timestamp < 30 * 60 * 1000) {
                    // Restore the search results and selected universities
                    if (pendingApp.results) {
                      setResults(pendingApp.results);
                      setCurrentStep(6); // Go to results step
                      scrollToTop();
                    }
                    if (pendingApp.selectedUniversities) {
                      setSelectedUniversities(new Set(pendingApp.selectedUniversities));
                    }
                    if (pendingApp.profile) {
                      setProfile(pendingApp.profile);
                    }
                    
                    // Check if documents are now ready
                    const hasIdentity = docsArray.some(d => d === 'aadhaar' || d === 'passport');
                    const hasAcademic = docsArray.some(d => ['10th_marksheet', '12th_marksheet', 'degree_certificate', 'transcript'].includes(d));
                    
                    if (hasIdentity && hasAcademic) {
                      toast.success('Documents uploaded! Ready to complete your application.');
                      // Auto-open the apply modal
                      setTimeout(() => {
                        setShowApplyModal(true);
                      }, 500);
                    } else {
                      toast.info('Documents loaded. Please ensure you have both identity and academic documents to apply.');
                    }
                  }
                  // Clear the pending application from storage
                  localStorage.removeItem('pendingApplication');
                } catch (e) {
                  console.error('Error restoring pending application:', e);
                  localStorage.removeItem('pendingApplication');
                }
              } else if (docsArray.length > 0) {
                toast.success('Documents loaded! You can now apply to universities.');
              }
            }
          }
        } catch (error) {
          console.error('Error refreshing documents:', error);
        }
      };
      refreshDocumentsAndRestoreState();
      
      // Clear the state so it doesn't refresh again on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.fromDocuments, user]);

  // Check if returning from GPA Calculator with calculated GPA
  useEffect(() => {
    // Map GPA Calculator destination IDs to AI Finder country names
    const destinationToCountryMap = {
      'usa': 'USA',
      'uk': 'UK',
      'canada': 'Canada',
      'australia': 'Australia',
      'germany': 'Germany',
      'new_zealand': 'New Zealand',
      'ireland': 'Ireland',
      'france': 'France',
      'netherlands': 'Netherlands',
      'dubai': 'Dubai',
      // Handle uppercase versions too
      'USA': 'USA',
      'UK': 'UK',
      'Canada': 'Canada',
      'Australia': 'Australia',
      'Germany': 'Germany',
      'New Zealand': 'New Zealand',
      'Ireland': 'Ireland',
      'Dubai': 'Dubai',
      'Dubai (UAE)': 'Dubai'
    };
    
    // Check immediately on mount and when location changes
    const checkForGpaData = () => {
      const gpaData = localStorage.getItem('calculated_gpa');
      const profileBackup = localStorage.getItem('ai_finder_profile_backup');
      
      if (gpaData) {
        try {
          const { gpa, scale, percentage, destination } = JSON.parse(gpaData);
          console.log('GPA Data from localStorage:', { gpa, scale, percentage, destination });
          
          // First restore the saved profile if available
          let restoredProfile = null;
          if (profileBackup) {
            try {
              restoredProfile = JSON.parse(profileBackup);
              console.log('Restored profile backup:', restoredProfile);
            } catch (e) {
              console.error('Error parsing profile backup:', e);
            }
          }
          
          if (gpa) {
            // Parse scale - could be "4.0", "7", "9", "10", "100", or a string like "4.0 GPA"
            let normalizedScale = 10; // default
            if (scale) {
              // Extract numeric part from scale (e.g., "4.0 GPA" -> 4.0, "4.0" -> 4.0)
              const scaleMatch = String(scale).match(/[\d.]+/);
              const parsedScale = scaleMatch ? parseFloat(scaleMatch[0]) : 10;
              
              // Map to the closest valid scale option (4, 5, 7, 9, 10, or 100)
              if (parsedScale <= 4) normalizedScale = 4;
              else if (parsedScale <= 5) normalizedScale = 5;
              else if (parsedScale <= 7) normalizedScale = 7;
              else if (parsedScale <= 9) normalizedScale = 9;
              else if (parsedScale <= 10) normalizedScale = 10;
              else normalizedScale = 100;
            }
            
            // Map destination to country name for preferred_countries
            const countryName = destinationToCountryMap[destination] || destinationToCountryMap[destination?.toLowerCase()];
            
            setProfile(prev => {
              // Start with restored profile if available, otherwise use current state
              const baseProfile = restoredProfile || prev;
              
              const updates = {
                ...baseProfile,
                gpa: gpa.toString(),
                gpa_scale: normalizedScale,
                original_percentage: percentage || null, // Store original percentage for cross-country conversion
                gpa_calculated_for: countryName || destination || null // Store which country GPA was calculated for
              };
              
              // Add destination country if valid and not already selected
              if (countryName && !baseProfile.preferred_countries.includes(countryName)) {
                updates.preferred_countries = [...baseProfile.preferred_countries, countryName];
              }
              
              return updates;
            });
            
            let successMessage = `GPA updated: ${gpa} on ${normalizedScale}-point scale`;
            if (countryName) {
              successMessage += ` • ${countryName} added to preferences`;
            }
            toast.success(successMessage, {
              description: percentage ? `Converted from ${percentage}%` : undefined
            });
          }
          // Clear the stored data after using it
          localStorage.removeItem('calculated_gpa');
          localStorage.removeItem('ai_finder_return');
          localStorage.removeItem('ai_finder_profile_backup');
        } catch (e) {
          console.error('Error parsing GPA data:', e);
        }
      }
    };
    
    // Check immediately
    checkForGpaData();
    
    // Also check after a short delay in case of race condition
    const timeoutId = setTimeout(checkForGpaData, 100);
    return () => clearTimeout(timeoutId);
  }, [location.key]); // location.key changes on every navigation

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/uac-desk/login');
    }
  }, [authLoading, isAuthenticated, navigate]);
  
  // Check search limits on load
  useEffect(() => {
    const checkSearchLimits = async () => {
      if (!user?.user_id) return;
      try {
        const res = await fetch(`${window.location.origin}/api/search/status/${user.user_id}`);
        if (res.ok) {
          const data = await res.json();
          setSearchStatus(data);
          if (data.is_blocked) {
            setShowLimitReachedModal(true);
          }
        }
      } catch (error) {
        console.error('Error checking search limits:', error);
      }
    };
    checkSearchLimits();
  }, [user]);
  
  // Fetch user documents for apply validation
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user?.user_id) return;
      try {
        const res = await fetch(`${window.location.origin}/api/counselor/profile/${user.user_id}`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          if (data.documents && typeof data.documents === 'object') {
            const docsArray = Object.entries(data.documents)
              .filter(([key, value]) => value && value.uploaded)
              .map(([key]) => key);
            setUserDocuments(docsArray);
          }
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };
    fetchDocuments();
  }, [user]);
  
  // Fetch suggested universities when study area is selected
  useEffect(() => {
    const fetchSuggestedUniversities = async () => {
      // Only fetch if we have a study area selected
      if (!profile.current_study_area) {
        setSuggestedUniversities([]);
        setShowSuggestions(false);
        return;
      }
      
      setLoadingSuggestions(true);
      try {
        // Map study area to search keywords for university API
        const studyAreaKeywords = {
          'Computer Science': 'computer science,software,IT',
          'AI & Machine Learning': 'artificial intelligence,machine learning,AI,data science',
          'Data Science': 'data science,analytics,statistics',
          'Business & MBA': 'business,MBA,management',
          'Engineering Mechanical': 'mechanical engineering',
          'Engineering Electrical': 'electrical engineering,electronics',
          'Engineering Civil': 'civil engineering,construction',
          'Medicine & Medical Science': 'medicine,medical,healthcare',
          'Law & Legal Studies': 'law,legal,LLM',
          'Architecture': 'architecture,design',
          'Psychology': 'psychology,behavioral science',
          'Economics': 'economics,finance',
          'Accounting & Finance': 'accounting,finance,CPA',
        };
        
        const searchTerm = studyAreaKeywords[profile.current_study_area] || profile.current_study_area.toLowerCase();
        
        // Fetch universities that match the study area
        const res = await fetch(
          `${window.location.origin}/api/university-pages/public/list?limit=4&search=${encodeURIComponent(searchTerm.split(',')[0])}`
        );
        
        if (res.ok) {
          const data = await res.json();
          if (data.universities && data.universities.length > 0) {
            setSuggestedUniversities(data.universities.slice(0, 4));
            setShowSuggestions(true);
          } else {
            // If no matches, get top universities as fallback
            const fallbackRes = await fetch(
              `${window.location.origin}/api/university-pages/public/list?limit=4`
            );
            if (fallbackRes.ok) {
              const fallbackData = await fallbackRes.json();
              setSuggestedUniversities(fallbackData.universities?.slice(0, 4) || []);
              setShowSuggestions(fallbackData.universities?.length > 0);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching suggested universities:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    };
    
    fetchSuggestedUniversities();
  }, [profile.current_study_area]);
  
  // Toggle university selection
  const toggleUniversitySelection = (index) => {
    setSelectedUniversities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        if (newSet.size >= 5) {
          toast.error('You can select up to 5 universities at a time');
          return prev;
        }
        newSet.add(index);
      }
      return newSet;
    });
  };
  
  // Check document requirements
  const hasIdentityDoc = userDocuments.some(d => d === 'aadhaar' || d === 'passport');
  const hasAcademicDoc = userDocuments.some(d => ['10th_marksheet', '12th_marksheet', 'degree_certificate', 'transcript'].includes(d));
  const canApply = hasIdentityDoc && hasAcademicDoc;
  
  // Handle apply to selected universities
  const handleApplyToSelected = async () => {
    if (!canApply) {
      toast.error('Please upload required documents (Identity + Academic) to apply');
      return;
    }
    
    if (selectedUniversities.size === 0) {
      toast.error('Please select at least one university');
      return;
    }
    
    const selectedUnis = results.recommendations.filter((_, idx) => selectedUniversities.has(idx));
    
    setApplying(true);
    try {
      // Create applications for each selected university
      for (const uni of selectedUnis) {
        await fetch(`${window.location.origin}/api/applications/create/${user.user_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            university_name: uni.university_name,
            program: uni.program || 'General Program',
            intake: 'Feb/July 2026',
            country: uni.country,
            notes: `Applied via AI University Finder. Match score: ${uni.match_score}%`
          })
        });
        
        // Log activity: Apply intent for each university
        if (user?.user_id) {
          logAIFinderApply(user.user_id, {
            name: uni.university_name,
            id: uni.id,
            program: uni.program,
            country: uni.country
          });
        }
      }
      
      toast.success(`Applied to ${selectedUnis.length} universities! Redirecting to Application Tracker...`);
      setShowApplyModal(false);
      setSelectedUniversities(new Set());
      
      // Navigate to application tracker
      setTimeout(() => {
        navigate('/uac-desk/applications');
      }, 1500);
    } catch (error) {
      console.error('Error applying:', error);
      toast.error('Failed to submit applications');
    } finally {
      setApplying(false);
    }
  };

  // Save selected universities as drafts or bookmarks
  const [savingToDrafts, setSavingToDrafts] = useState(false);
  
  const handleSaveToDrafts = async (status = 'draft') => {
    if (!user?.user_id) {
      toast.error('Please login to save');
      return;
    }
    
    if (selectedUniversities.size === 0) {
      toast.error('Please select at least one university');
      return;
    }
    
    const selectedUnis = results.recommendations.filter((_, idx) => selectedUniversities.has(idx));
    
    setSavingToDrafts(true);
    let savedCount = 0;
    let totalAutoAttached = 0;
    let totalDigilockerAttached = 0;

    try {
      for (const uni of selectedUnis) {
        const res = await fetch(`${window.location.origin}/api/applications/draft/${user.user_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            university_name: uni.university_name,
            program: uni.program || 'General Program',
            country: uni.country,
            status: status, // 'draft' or 'bookmarked'
            match_score: uni.match_score,
            notes: `Saved from AI Finder. Match: ${uni.match_score}%`
          })
        });

        if (res.ok) {
          savedCount++;
          try {
            const data = await res.json();
            totalAutoAttached += data?.auto_attached_documents?.count || 0;
            totalDigilockerAttached += data?.auto_attached_documents?.digilocker_verified_count || 0;
          } catch (_) { /* response parse — ignore */ }
        }
      }

      const action = status === 'draft' ? 'saved as drafts' : 'bookmarked for later';
      toast.success(
        <div>
          <p className="font-medium">{savedCount} course{savedCount > 1 ? 's' : ''} {action}!</p>
          {totalAutoAttached > 0 && (
            <p className="text-sm text-emerald-300">
              {totalAutoAttached} doc{totalAutoAttached === 1 ? '' : 's'} auto-attached
              {totalDigilockerAttached > 0 ? ` (${totalDigilockerAttached} DigiLocker-verified)` : ''}
            </p>
          )}
          <p className="text-sm text-white/70">View in My Applications → {status === 'draft' ? 'Drafts' : 'Saved for Later'}</p>
        </div>
      );

      setSelectedUniversities(new Set());
      setShowApplyModal(false);

    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSavingToDrafts(false);
    }
  };
  
  // Handle "Learn More" - search for university and open in new tab
  const handleLearnMore = async (universityName, country) => {
    try {
      // Search for the university by name
      const searchTerm = universityName.split(' ').slice(0, 3).join(' '); // Use first 3 words for search
      const res = await fetch(
        `${window.location.origin}/api/university-pages/public/list?search=${encodeURIComponent(searchTerm)}&country=${encodeURIComponent(country || '')}&limit=5`
      );
      
      if (res.ok) {
        const data = await res.json();
        if (data.universities && data.universities.length > 0) {
          // Find best match - check if name contains the search term
          const match = data.universities.find(u => 
            u.university_name?.toLowerCase().includes(universityName.toLowerCase().split(' ')[0]) &&
            (!country || u.country?.toLowerCase() === country.toLowerCase())
          ) || data.universities.find(u => 
            u.university_name?.toLowerCase() === universityName.toLowerCase()
          );
          
          if (match) {
            // Open in new tab
            window.open(`/universities/${match.slug}`, '_blank', 'noopener,noreferrer');
            return;
          }
        }
      }
      
      // Fallback: Open Google search for the university's official website
      const searchQuery = encodeURIComponent(`${universityName} ${country || ''} official website admissions`);
      const link = document.createElement('a');
      link.href = `https://www.google.com/search?q=${searchQuery}`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.info(`Opening search for ${universityName}. We're adding more university profiles soon!`);
      
    } catch (error) {
      console.error('Error finding university:', error);
      // Fallback to Google search on error
      const searchQuery = encodeURIComponent(`${universityName} ${country || ''} official website`);
      window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
    }
  };
  
  // Save university to student's saved list
  const handleSaveUniversity = async (uni, index) => {
    if (!user?.user_id) {
      toast.error('Please login to save universities');
      return;
    }
    
    // Generate slug from university name
    const slug = uni.university_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    
    if (savedUniversities.has(index)) {
      // Already saved - unsave it
      setSavingUniversity(index);
      try {
        const res = await fetch(
          `${window.location.origin}/api/university-pages/saved/${user.user_id}/${slug}`,
          { method: 'DELETE', credentials: 'include' }
        );
        if (res.ok) {
          setSavedUniversities(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
          });
          toast.success('Removed from saved universities');
        }
      } catch (error) {
        console.error('Error removing university:', error);
        toast.error('Failed to remove');
      } finally {
        setSavingUniversity(null);
      }
    } else {
      // Save new university
      setSavingUniversity(index);
      try {
        const res = await fetch(
          `${window.location.origin}/api/university-pages/saved/${user.user_id}/${slug}`,
          { method: 'POST', credentials: 'include' }
        );
        if (res.ok) {
          setSavedUniversities(prev => new Set([...prev, index]));
          toast.success(
            <div>
              <p className="font-medium">Saved to your list!</p>
              <p className="text-sm text-white/70">View in Dashboard → Saved Universities</p>
            </div>
          );
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Failed to save');
        }
      } catch (error) {
        console.error('Error saving university:', error);
        toast.error('Failed to save university');
      } finally {
        setSavingUniversity(null);
      }
    }
  };
  
  // Save current search results
  const handleSaveSearch = async () => {
    if (!results) {
      toast.error('No search results to save');
      return;
    }
    
    setSavingSearch(true);
    try {
      // Generate a meaningful search name based on the search parameters
      const countryPart = profile.preferred_countries.length > 0 
        ? profile.preferred_countries.slice(0, 3).join(', ') 
        : 'Global';
      const degreePart = profile.target_degree 
        ? profile.target_degree.charAt(0).toUpperCase() + profile.target_degree.slice(1)
        : '';
      const datePart = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const searchName = `${countryPart} ${degreePart} - ${datePart}`.trim();
      
      const res = await fetch(`${window.location.origin}/api/ai/searches/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          search_name: searchName,
          profile: profile,
          results: results
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSearchSaved(true);
        toast.success(data.message || 'Search saved! View it anytime from your dashboard.');
        
        // Log activity: Search saved
        if (user?.user_id) {
          logSearchSaved(user.user_id, searchName, {
            countries: profile.preferred_countries,
            majors: profile.preferred_majors,
            target_degree: profile.target_degree
          });
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save search');
      }
    } catch (error) {
      console.error('Error saving search:', error);
      toast.error('Failed to save search');
    } finally {
      setSavingSearch(false);
    }
  };

  const handleCountryToggle = (country) => {
    setProfile(prev => ({
      ...prev,
      preferred_countries: prev.preferred_countries.includes(country)
        ? prev.preferred_countries.filter(c => c !== country)
        : [...prev.preferred_countries, country]
    }));
  };

  // Handle "Verify Fee" click - logs intent and opens university fee search
  const handleVerifyFee = async (uni) => {
    // Open the search first (before async call) to avoid popup blocker
    const searchQuery = encodeURIComponent(
      `${uni.university_name} ${uni.program || ''} international student tuition fees ${new Date().getFullYear()}`
    );
    const searchUrl = `https://www.google.com/search?q=${searchQuery}`;
    
    // Create and click a link element (more reliable than window.open)
    const link = document.createElement('a');
    link.href = searchUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Show toast notification
    toast.success(`Looking up fees for ${uni.university_name}`, {
      description: 'A new tab has opened with fee information.'
    });
    
    // Log this as a high-intent activity (async, after tab opens)
    if (user?.user_id) {
      try {
        await fetch(`${window.location.origin}/api/activity/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            user_id: user.user_id,
            activity_type: 'fee_verification_requested',
            details: {
              university_name: uni.university_name,
              country: uni.country,
              program: uni.program,
              indicative_fee: uni.tuition_local || uni.tuition_usd,
              currency: uni.tuition_currency || 'USD'
            }
          })
        });
      } catch (err) {
        console.error('Failed to log fee verification activity:', err);
      }
    }
  };

  const handleFieldToggle = (field) => {
    setProfile(prev => ({
      ...prev,
      preferred_majors: prev.preferred_majors.includes(field)
        ? prev.preferred_majors.filter(f => f !== field)
        : [...prev.preferred_majors, field]
    }));
  };

  const handleSubmit = async () => {
    // Check search limits first
    if (searchStatus?.is_blocked) {
      setShowLimitReachedModal(true);
      return;
    }
    
    setLoading(true);
    setLoadingMessage(loadingMessages[0]);
    
    // Log activity: AI Finder search initiated
    if (user?.user_id) {
      logAIFinderSearch(user.user_id, {
        targetDegree: profile.target_degree,
        countries: profile.preferred_countries,
        majors: profile.preferred_majors,
        budgetRange: { min: profile.budget_min, max: profile.budget_max },
        testScores: {
          gpa: profile.gpa,
          ielts: profile.ielts_score,
          toefl: profile.toefl_score,
          gre: profile.gre_score,
          gmat: profile.gmat_score
        }
      });
    }
    
    // Cycle through loading messages
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[messageIndex]);
    }, 2500);
    
    try {
      // Create search session before making the search
      const sessionRes = await fetch(`${window.location.origin}/api/search/session/start?user_id=${user.user_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          search_type: 'ai_finder',
          course: profile.preferred_majors.join(', ') || null,
          destination: profile.preferred_countries.join(', ') || null
        })
      });
      
      if (sessionRes.status === 403) {
        setShowLimitReachedModal(true);
        setLoading(false);
        return;
      }
      
      // Store the session ID for later saving
      const sessionData = await sessionRes.json();
      setCurrentSessionId(sessionData.session_id);
      setSearchSaved(false);
      
      const response = await fetch(`${window.location.origin}/api/ai/find-universities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          profile: {
            current_degree: profile.current_degree || null,
            current_study_area: profile.current_study_area || null,
            selected_fields: profile.selected_fields || [],
            field_of_study: profile.field_of_study || null,
            gpa: profile.gpa ? parseFloat(profile.gpa) : null,
            gpa_scale: profile.gpa_scale || 10,
            original_percentage: profile.original_percentage || null,
            gpa_calculated_for: profile.gpa_calculated_for || null,
            gre_score: profile.gre_score ? parseInt(profile.gre_score) : null,
            gmat_score: profile.gmat_score ? parseInt(profile.gmat_score) : null,
            ielts_score: profile.ielts_score ? parseFloat(profile.ielts_score) : null,
            toefl_score: profile.toefl_score ? parseInt(profile.toefl_score) : null,
            pte_score: profile.pte_score ? parseFloat(profile.pte_score) : null,
            sat_score: profile.sat_score ? parseInt(profile.sat_score) : null,
            target_degree: profile.target_degree || 'masters',
            preferred_countries: profile.preferred_countries || [],
            preferred_majors: profile.preferred_majors || [],
            budget_min: profile.budget_min || null,
            budget_max: profile.budget_max || null,
            needs_scholarship: profile.needs_scholarship || false,
            career_goals: profile.career_goals || null,
            work_experience_years: profile.work_experience_years || 0,
            priority_ranking: profile.priority_ranking || 3,
            priority_cost: profile.priority_cost || 3,
            priority_location: profile.priority_location || 3,
            priority_course: profile.priority_course || 3,
            priority_career: profile.priority_career || 3,
            priority_pr: profile.priority_pr || 3,
            priority_culture: profile.priority_culture || 3,
            // Include user's pre-selected persona for AI context
            user_persona: user?.primary_motivation || null
          },
          max_recommendations: 8
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get recommendations');
      }

      const data = await response.json();
      setResults(data);
      
      // Log activity: AI Finder results received
      if (user?.user_id) {
        logAIFinderResults(
          user.user_id,
          data.universities?.length || 0,
          data.persona
        );
      }
    } catch (err) {
      console.error('AI recommendation error:', err);
    } finally {
      clearInterval(messageInterval);
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const getPersonaIcon = (persona) => {
    const icons = {
      // Legacy
      ranking_hunter: Trophy,
      roi_seeker: TrendingUp,
      smart_spender: PiggyBank,
      global_citizen: Globe,
      // New personas
      'course-known': GraduationCap,
      'budget-clarity': PiggyBank,
      'country-help': Globe,
      'career-focus': TrendingUp,
      'ai-guided': Sparkles
    };
    return icons[persona] || Star;
  };

  const getPersonaColor = (persona) => {
    const colors = {
      // Legacy
      ranking_hunter: 'from-amber-500 to-orange-600',
      roi_seeker: 'from-emerald-500 to-teal-600',
      smart_spender: 'from-violet-500 to-purple-600',
      global_citizen: 'from-cyan-500 to-blue-600',
      // New personas
      'course-known': 'from-ux-cyan to-ux-blue',
      'budget-clarity': 'from-emerald-400 to-teal-500',
      'country-help': 'from-ux-violet to-ux-rose',
      'career-focus': 'from-amber-400 to-orange-500',
      'ai-guided': 'from-ux-cyan to-ux-violet'
    };
    return colors[persona] || 'from-ux-cyan to-ux-blue';
  };

  // Get personalized intro message based on user's motivation
  const getPersonaIntroMessage = () => {
    const persona = user?.primary_motivation;
    const messages = {
      'course-known': {
        title: "Let's find the perfect university for your course!",
        subtitle: "Since you know what you want to study, we'll focus on finding top-ranked programs in your field.",
        icon: GraduationCap,
        gradient: 'from-ux-cyan to-ux-blue'
      },
      'budget-clarity': {
        title: "Let's find affordable quality programs!",
        subtitle: "We'll prioritize cost-effective options with scholarships and financial aid opportunities.",
        icon: PiggyBank,
        gradient: 'from-emerald-400 to-teal-500'
      },
      'country-help': {
        title: "Let's explore global study destinations!",
        subtitle: "We'll help you compare countries and find the best fit for your lifestyle and goals.",
        icon: Globe,
        gradient: 'from-ux-violet to-ux-rose'
      },
      'career-focus': {
        title: "Let's maximize your career outcomes!",
        subtitle: "We'll focus on programs with strong placement records and ROI potential.",
        icon: TrendingUp,
        gradient: 'from-amber-400 to-orange-500'
      },
      'ai-guided': {
        title: "Let AI guide your perfect match!",
        subtitle: "Answer a few questions and we'll find universities tailored just for you.",
        icon: Sparkles,
        gradient: 'from-ux-cyan to-ux-violet'
      }
    };
    return messages[persona] || null;
  };


  if (authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  // Results View
  if (results) {
    const PersonaIcon = getPersonaIcon(results.detected_persona);
    
    return (
      <div className={`min-h-screen w-full max-w-[100vw] overflow-x-hidden ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
        {/* Header */}
        <header className={`sticky top-0 z-50 border-b w-full ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-ux-slate-light'}`}>
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/uac-desk/dashboard')} className="flex-shrink-0">
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button 
                  variant={searchSaved ? "secondary" : "outline"}
                  size="sm"
                  onClick={handleSaveSearch}
                  disabled={savingSearch || searchSaved}
                  className={`flex-shrink-0 ${searchSaved ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}`}
                  data-testid="save-search-btn"
                >
                  {savingSearch ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : searchSaved ? (
                    <BookmarkCheck className="w-4 h-4 sm:mr-2" />
                  ) : (
                    <Bookmark className="w-4 h-4 sm:mr-2" />
                  )}
                  <span className="hidden sm:inline">{searchSaved ? 'Saved' : 'Save'}</span>
                </Button>
                <Button variant="outline" size="sm" onClick={() => setResults(null)} className="flex-shrink-0">
                  <span className="hidden sm:inline">Search Again</span>
                  <span className="sm:hidden">New</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-28" style={{ maxWidth: '100%' }}>
          {/* Persona Result */}
          <div className={`glass-panel rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 bg-gradient-to-r ${getPersonaColor(results.detected_persona).replace('from-', 'from-').replace('to-', 'to-')}/20`}>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mb-4 text-center sm:text-left">
              <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${getPersonaColor(results.detected_persona)} flex items-center justify-center flex-shrink-0`}>
                <PersonaIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <Badge className="mb-2 bg-white/20 text-white border-0">Your Persona</Badge>
                <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {results.detected_persona.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </h2>
              </div>
            </div>
            <p className={`text-sm sm:text-base ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
              {results.persona_description}
            </p>
          </div>

          {/* Advice */}
          {results.advice && (
            <div className={`glass-panel rounded-2xl p-6 mb-8 ${isDark ? '' : 'bg-white shadow-sm'}`}>
              <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                💡 Personalized Advice
              </h3>
              <p className={`${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                {results.advice}
              </p>
            </div>
          )}

          {/* University Categories Legend */}
          {(() => {
            // Categorize universities based on match_score
            const ambitious = results.recommendations.filter(u => u.match_score < 70);
            const target = results.recommendations.filter(u => u.match_score >= 70 && u.match_score < 85);
            const safe = results.recommendations.filter(u => u.match_score >= 85);
            
            return (
              <>
                {/* Category Summary */}
                <div className={`flex flex-wrap gap-3 mb-6 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500" />
                    <span className={`text-sm ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                      Ambitious ({ambitious.length})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" />
                    <span className={`text-sm ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                      Target ({target.length})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500" />
                    <span className={`text-sm ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                      Safe ({safe.length})
                    </span>
                  </div>
                  <span className={`ml-auto text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                    Based on your profile match
                  </span>
                </div>

                {/* Recommendations Grid */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Recommended Universities ({results.recommendations.length})
                  </h3>
                  {selectedUniversities.size > 0 && (
                    <Badge className="bg-ux-violet text-white">
                      {selectedUniversities.size} selected
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 pb-24">
                  {results.recommendations.map((uni, index) => {
                    const UniPersonaIcon = getPersonaIcon(uni.persona_fit);
                    const isSelected = selectedUniversities.has(index);
                    // Determine category
                    const category = uni.match_score < 70 ? 'ambitious' : uni.match_score < 85 ? 'target' : 'safe';
                    const categoryColors = {
                      ambitious: 'from-amber-500 to-orange-500',
                      target: 'from-emerald-500 to-teal-500',
                      safe: 'from-blue-500 to-cyan-500'
                    };
                    const categoryLabels = {
                      ambitious: 'Ambitious',
                      target: 'Target',
                      safe: 'Safe'
                    };
                    return (
                      <div 
                        key={index}
                        className={`glass-panel rounded-xl sm:rounded-2xl p-4 sm:p-6 relative transition-all duration-200 overflow-hidden w-full ${
                          isSelected 
                            ? 'ring-2 ring-ux-violet ring-offset-2 ring-offset-transparent' 
                            : ''
                        } ${isDark ? '' : 'bg-white shadow-sm'}`}
                        data-testid={`university-card-${index}`}
                      >
                        {/* Category Badge */}
                        <div className={`absolute top-3 sm:top-4 left-3 sm:left-4 px-2 py-0.5 rounded-full text-xs font-medium text-white bg-gradient-to-r ${categoryColors[category]}`}>
                          {categoryLabels[category]}
                        </div>
                        
                        {/* Selection Checkbox */}
                        <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-10">
                          <Checkbox
                            id={`select-uni-${index}`}
                            checked={isSelected}
                            onCheckedChange={() => toggleUniversitySelection(index)}
                            className="h-5 w-5 border-2 data-[state=checked]:bg-ux-violet data-[state=checked]:border-ux-violet"
                            data-testid={`select-university-${index}`}
                          />
                        </div>
                        
                        {/* University Info */}
                        <div className="flex items-start justify-between mb-3 sm:mb-4 pr-8 pt-5 sm:pt-6">
                          <div className="min-w-0 flex-1">
                            <h4 className={`font-bold text-base sm:text-lg truncate ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                              {uni.university_name}
                            </h4>
                            <p className={`text-xs sm:text-sm truncate ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                              {uni.country} • {uni.program}
                            </p>
                          </div>
                        </div>
                        
                        {/* Match Score & Ranking */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <div className={`flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full bg-gradient-to-r ${getPersonaColor(uni.persona_fit)} text-white text-xs font-medium`}>
                            <UniPersonaIcon className="w-3 h-3" />
                            {uni.match_score}%
                          </div>
                          {uni.ranking && (
                            <span className={`flex items-center gap-1 text-xs sm:text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                              <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-amber-500" />
                              #{uni.ranking}
                            </span>
                          )}
                        </div>

                        {/* Tuition */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3 sm:mb-4 text-xs sm:text-sm">
                          {(uni.tuition_local || uni.tuition_usd) && (
                            <>
                              <span className={`flex items-center gap-1 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`} title="Indicative fee - verify with university">
                                <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
                                {formatTuition(uni)}
                                <span className="text-xs text-amber-500 font-medium">*</span>
                              </span>
                              <button
                                onClick={() => handleVerifyFee(uni)}
                                className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                  isDark 
                                    ? 'border-ux-cyan/50 text-ux-cyan hover:bg-ux-cyan/20' 
                                    : 'border-ux-cyan text-ux-cyan hover:bg-ux-cyan/10'
                                }`}
                              >
                                Verify Fee
                              </button>
                            </>
                          )}
                        </div>

                        {/* Reasons */}
                        <div className="space-y-3">
                          <div>
                            <p className={`text-xs font-medium mb-1 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                              Why this fits you:
                            </p>
                            <ul className="space-y-1">
                              {uni.reasons.slice(0, 2).map((reason, i) => (
                                <li key={i} className={`text-sm flex items-start gap-2 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                                  <CheckCircle2 className="w-4 h-4 text-ux-green flex-shrink-0 mt-0.5" />
                                  {reason}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-4 pt-4 border-t border-white/10 flex gap-2">
                          <Button 
                            variant={savedUniversities.has(index) ? "secondary" : "outline"} 
                            size="sm" 
                            className={`flex-1 ${savedUniversities.has(index) ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}
                            onClick={() => handleSaveUniversity(uni, index)}
                            disabled={savingUniversity === index}
                          >
                            {savingUniversity === index ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : savedUniversities.has(index) ? (
                              <BookmarkCheck className="w-4 h-4 mr-1" />
                            ) : (
                              <Bookmark className="w-4 h-4 mr-1" />
                            )}
                            {savedUniversities.has(index) ? 'Saved' : 'Save'}
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-gradient-to-r from-ux-cyan to-ux-blue"
                            onClick={() => handleLearnMore(uni.university_name, uni.country)}
                          >
                            Learn More
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* Floating Apply Bar */}
          {selectedUniversities.size > 0 && (
            <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-gradient-to-t from-black/90 to-black/70 backdrop-blur-lg border-t border-white/10" style={{ width: '100%', maxWidth: '100vw' }}>
              <div className="w-full max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 px-1">
                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-center sm:justify-start">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-ux-violet flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-white font-medium text-sm">{selectedUniversities.size} selected</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto justify-center flex-wrap">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedUniversities(new Set())}
                    className="text-white/70 hover:text-white flex-shrink-0 px-2"
                    data-testid="clear-selection-btn"
                  >
                    Clear
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCompareModal(true)}
                    className="hidden sm:flex border-white/30 text-white hover:bg-white/10 px-3"
                    data-testid="compare-btn"
                  >
                    Compare
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => handleSaveToDrafts('bookmarked')}
                    disabled={savingToDrafts}
                    className="border-white/30 text-white hover:bg-white/10 px-2 sm:px-3"
                    data-testid="bookmark-btn"
                  >
                    <BookmarkPlus className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Save for Later</span>
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => handleSaveToDrafts('draft')}
                    disabled={savingToDrafts}
                    className="bg-amber-500 hover:bg-amber-600 text-white px-2 sm:px-3"
                    data-testid="draft-btn"
                  >
                    {savingToDrafts ? (
                      <Loader2 className="w-4 h-4 animate-spin sm:mr-1" />
                    ) : (
                      <FileText className="w-4 h-4 sm:mr-1" />
                    )}
                    <span className="hidden sm:inline">Draft to Apply</span>
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setShowApplyModal(true)}
                    className="bg-gradient-to-r from-ux-violet to-purple-600 hover:opacity-90 text-white px-2 sm:px-3"
                    data-testid="apply-selected-btn"
                  >
                    <Send className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Apply Now</span>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Apply Confirmation Modal */}
          <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
            <DialogContent className={`max-w-lg ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>
                  Apply to {selectedUniversities.size} Universit{selectedUniversities.size === 1 ? 'y' : 'ies'}
                </DialogTitle>
                <DialogDescription className={isDark ? 'text-white/60' : ''}>
                  Review your selection and confirm your applications
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                {/* Selected Universities List */}
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {results?.recommendations
                    .filter((_, idx) => selectedUniversities.has(idx))
                    .map((uni, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg flex items-center justify-between ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                      >
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                            {uni.university_name}
                          </p>
                          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                            {uni.country} • {uni.program}
                          </p>
                        </div>
                        <Badge className="bg-ux-violet/20 text-ux-violet border-0">
                          {uni.match_score}% match
                        </Badge>
                      </div>
                    ))
                  }
                </div>

                {/* Document Check */}
                <div className={`p-4 rounded-lg ${canApply ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                  <div className="flex items-start gap-3">
                    {canApply ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium ${canApply ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {canApply ? 'Documents Ready' : 'Documents Required'}
                      </p>
                      <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                        {canApply 
                          ? 'You have uploaded the required identity and academic documents.' 
                          : 'Please upload at least one identity document (Aadhaar/Passport) and one academic document (Marksheet/Certificate) to apply.'}
                      </p>
                      {!canApply && (
                        <Button 
                          variant="link" 
                          className="p-0 h-auto mt-2 text-amber-600"
                          onClick={() => {
                            // Save selected universities and results to localStorage before navigating
                            const pendingApplication = {
                              selectedUniversities: Array.from(selectedUniversities),
                              results: results,
                              profile: profile,
                              timestamp: Date.now()
                            };
                            localStorage.setItem('pendingApplication', JSON.stringify(pendingApplication));
                            
                            setShowApplyModal(false);
                            navigate('/uac-desk/documents', { 
                              state: { returnTo: '/uac-desk/ai-finder', pendingApplication: true } 
                            });
                          }}
                        >
                          Upload Documents →
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowApplyModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleApplyToSelected}
                  disabled={!canApply || applying}
                  className="bg-gradient-to-r from-ux-violet to-purple-600"
                  data-testid="confirm-apply-btn"
                >
                  {applying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Confirm & Apply
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Compare Modal - Full Screen */}
          <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
            <DialogContent className={`max-w-6xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#1a2830] border-white/10' : 'bg-white'}`}>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className={`text-xl ${isDark ? 'text-white' : ''}`}>
                      Compare Universities
                    </DialogTitle>
                    <DialogDescription className={isDark ? 'text-white/60' : ''}>
                      Side-by-side comparison of {selectedUniversities.size} selected universities
                    </DialogDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCompareModal(false)}
                    className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Results
                  </Button>
                </div>
              </DialogHeader>
              
              {/* Comparison Table */}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <th className={`text-left p-3 font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        Attribute
                      </th>
                      {results?.recommendations?.filter((_, i) => selectedUniversities.has(i)).map((uni, idx) => (
                        <th key={idx} className={`text-center p-3 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-sm">{uni.university_name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-500'}`}>
                              {uni.country}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Ranking Row */}
                    <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-amber-500" />
                          World Ranking
                        </div>
                        <span className="text-xs text-white/40">(QS 2027)</span>
                      </td>
                      {results?.recommendations?.filter((_, i) => selectedUniversities.has(i)).map((uni, idx) => (
                        <td key={idx} className={`text-center p-3 font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                          #{uni.ranking || 'N/A'}
                        </td>
                      ))}
                    </tr>
                    {/* Program Row */}
                    <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-ux-violet" />
                          Program
                        </div>
                      </td>
                      {results?.recommendations?.filter((_, i) => selectedUniversities.has(i)).map((uni, idx) => (
                        <td key={idx} className={`text-center p-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {uni.program}
                        </td>
                      ))}
                    </tr>
                    {/* Tuition Row */}
                    <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-green-500" />
                          Annual Tuition
                        </div>
                      </td>
                      {results?.recommendations?.filter((_, i) => selectedUniversities.has(i)).map((uni, idx) => (
                        <td key={idx} className={`text-center p-3 font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          {formatTuition(uni) || 'N/A'}
                        </td>
                      ))}
                    </tr>
                    {/* Match Score Row */}
                    <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-ux-cyan" />
                          Match Score
                        </div>
                      </td>
                      {results?.recommendations?.filter((_, i) => selectedUniversities.has(i)).map((uni, idx) => (
                        <td key={idx} className="text-center p-3">
                          <div className="flex justify-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              uni.match_score >= 85 
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : uni.match_score >= 70
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {uni.match_score}% {uni.match_score >= 85 ? '(Safe)' : uni.match_score >= 70 ? '(Target)' : '(Ambitious)'}
                            </span>
                          </div>
                        </td>
                      ))}
                    </tr>
                    {/* Persona Fit Row */}
                    <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-purple-400" />
                          Best For
                        </div>
                      </td>
                      {results?.recommendations?.filter((_, i) => selectedUniversities.has(i)).map((uni, idx) => (
                        <td key={idx} className={`text-center p-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          {uni.persona_fit?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'All Students'}
                        </td>
                      ))}
                    </tr>
                    {/* Pros Row */}
                    <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-3 font-medium align-top ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4 text-green-400" />
                          Pros
                        </div>
                      </td>
                      {results?.recommendations?.filter((_, i) => selectedUniversities.has(i)).map((uni, idx) => (
                        <td key={idx} className={`p-3 align-top ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          <ul className="text-xs space-y-1">
                            {uni.pros?.slice(0, 3).map((pro, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-green-400 mt-0.5">+</span>
                                <span>{pro}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      ))}
                    </tr>
                    {/* Cons Row */}
                    <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-3 font-medium align-top ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <ThumbsDown className="w-4 h-4 text-red-400" />
                          Cons
                        </div>
                      </td>
                      {results?.recommendations?.filter((_, i) => selectedUniversities.has(i)).map((uni, idx) => (
                        <td key={idx} className={`p-3 align-top ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          <ul className="text-xs space-y-1">
                            {uni.cons?.slice(0, 2).map((con, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-red-400 mt-0.5">-</span>
                                <span>{con}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className={`mt-4 p-3 rounded-lg text-center text-xs ${isDark ? 'bg-white/5 text-white/50' : 'bg-gray-50 text-gray-500'}`}>
                Rankings source: QS World University Rankings 2025. Tuition and statistics are estimates and may vary by program.
              </div>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setShowCompareModal(false)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Results
                </Button>
                <Button 
                  onClick={() => {
                    setShowCompareModal(false);
                    setShowApplyModal(true);
                  }}
                  className="bg-gradient-to-r from-ux-violet to-purple-600"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Apply to Selected
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Fee Disclaimer */}
          <div className={`mt-4 text-center text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
            <p>* Tuition fees shown are indicative estimates. Actual fees may vary by program and year. Please verify with the university directly.</p>
          </div>

          {/* Loan Eligibility CTA - After Results */}
          <div className={`mt-8 glass-panel rounded-2xl p-6 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 border border-emerald-500/30 ${isDark ? '' : 'shadow-lg'}`}>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <Banknote className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className={`font-bold text-lg mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  Planning to Fund Your Studies?
                </h3>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  Check your education loan eligibility in 2 minutes and get an indicative estimate of how much you may qualify for.
                </p>
              </div>
              <Button 
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white whitespace-nowrap"
                onClick={() => navigate('/uac-desk/loan-eligibility')}
                data-testid="loan-cta-btn"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Check Loan Eligibility
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Form View
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
      {/* Scroll anchor for mobile - positioned at absolute top */}
      <div id="ai-finder-top" className="absolute top-0 left-0" aria-hidden="true" />
      
      {/* Loading Overlay - Centered */}
      {loading && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-gradient-to-b from-white/10 to-transparent rounded-3xl p-8 text-center">
            {/* Animated Logo/Icon */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-ux-violet to-ux-cyan animate-spin" style={{ animationDuration: '3s' }} />
              <div className="absolute inset-1 rounded-full bg-black/80 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full h-1.5 bg-white/20 rounded-full mb-5 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-ux-cyan via-ux-violet to-ux-rose rounded-full"
                style={{ 
                  width: '100%',
                  animation: 'shimmer 2s ease-in-out infinite'
                }} 
              />
            </div>
            
            {/* Loading Message */}
            <p className="text-white text-base sm:text-lg font-medium mb-1">
              {loadingMessage || 'Finding your perfect universities...'}
            </p>
            <p className="text-white/50 text-xs sm:text-sm">
              This usually takes 10-15 seconds
            </p>
            
            {/* Animated Dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              <div className="w-2 h-2 bg-ux-cyan rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-ux-violet rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-ux-rose rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-ux-slate-light'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/uac-desk/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <img 
                src={getLogo()} 
                alt="UniXperts" 
                className={`h-8 object-contain ${isDark ? 'brightness-0 invert' : ''}`}
              />
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-ux-cyan" />
                <span className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  AI University Finder
                </span>
              </div>
            </div>
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className={`border-b ${isDark ? 'border-white/10' : 'border-ux-slate-light'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}
              >
                <button
                  onClick={() => { if (currentStep > step.id) { setCurrentStep(step.id); scrollToTop(); } }}
                  className={`flex items-center gap-2 ${
                    currentStep >= step.id 
                      ? 'text-ux-cyan' 
                      : isDark ? 'text-white/30' : 'text-ux-charcoal/30'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step.id 
                      ? 'bg-ux-cyan text-white' 
                      : isDark ? 'bg-white/10 text-white/30' : 'bg-ux-slate-light text-ux-charcoal/30'
                  }`}>
                    {currentStep > step.id ? '✓' : step.id}
                  </div>
                  <span className="hidden md:block text-sm font-medium">{step.title}</span>
                </button>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    currentStep > step.id 
                      ? 'bg-ux-cyan' 
                      : isDark ? 'bg-white/10' : 'bg-ux-slate-light'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24">
        <div className={`glass-panel rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 ${isDark ? '' : 'bg-white shadow-lg'}`}>
          
          {/* Step 1: Academic Background */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Persona-Aware Hero Section */}
              {(() => {
                const personaIntro = getPersonaIntroMessage();
                if (personaIntro) {
                  const PersonaIcon = personaIntro.icon;
                  return (
                    <div className={`p-6 rounded-2xl bg-gradient-to-r ${personaIntro.gradient}/10 border border-white/10 mb-4`}>
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${personaIntro.gradient} flex items-center justify-center flex-shrink-0`}>
                          <PersonaIcon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className={`text-xl md:text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                            {personaIntro.title}
                          </h2>
                          <p className={`${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                            {personaIntro.subtitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Default Hero Section (shown if no persona) */}
              {!getPersonaIntroMessage() && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-indigo-500/10 mb-4">
                    <GraduationCap className="w-5 h-5 text-blue-500" />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      Step 1 of 5
                    </span>
                  </div>
                  <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Let's start with you!
                  </h2>
                  <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    Tell us about your academic journey so far
                  </p>
                </div>
              )}
              
              {/* Step indicator for persona users */}
              {getPersonaIntroMessage() && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-indigo-500/10">
                    <GraduationCap className="w-5 h-5 text-blue-500" />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      Step 1 of 5 — Tell us about your academic background
                    </span>
                  </div>
                </div>
              )}
              
              {/* Current Education Section */}
              <div className={`p-5 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Current / Completed Education</h3>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Your most recent or ongoing qualification</p>
                  </div>
                </div>
              
              {/* Degree Type and Course Name */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="relative" ref={degreeTypeRef}>
                  <Label className={isDark ? 'text-white/70' : ''}>Current Degree <span className="text-red-400">*</span></Label>
                  <div className="relative mt-1">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`} />
                    <Input
                      placeholder="Search (e.g., 12th, B.Tech, MBA...)"
                      value={degreeTypeSearch || profile.current_degree}
                      onChange={(e) => {
                        setDegreeTypeSearch(e.target.value);
                        setShowDegreeTypeDropdown(true);
                      }}
                      onFocus={() => setShowDegreeTypeDropdown(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && showDegreeTypeDropdown) {
                          e.preventDefault();
                          const filtered = degreeTypes.filter(degree => 
                            !degreeTypeSearch || degree.toLowerCase().includes(degreeTypeSearch.toLowerCase())
                          );
                          if (filtered.length > 0) {
                            const suggestedTarget = getSuggestedTargetDegree(filtered[0]);
                            setProfile({ 
                              ...profile, 
                              current_degree: filtered[0], 
                              field_of_study: '',
                              target_degree: suggestedTarget
                            });
                            setDegreeTypeSearch('');
                            setShowDegreeTypeDropdown(false);
                          }
                        }
                      }}
                      className={`pl-9 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                    />
                    {profile.current_degree && !degreeTypeSearch && (
                      <button
                        onClick={() => {
                          // Also clear specialization and reset target degree when degree is cleared
                          setProfile({ ...profile, current_degree: '', field_of_study: '', target_degree: 'masters' });
                          setDegreeTypeSearch('');
                          setSpecializationSearch('');
                        }}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40 hover:text-white' : 'text-ux-charcoal/40 hover:text-ux-charcoal'}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Degree Type Dropdown */}
                  {showDegreeTypeDropdown && (
                    <div 
                      className={`absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-lg border shadow-lg ${
                        isDark ? 'bg-[#233842] border-white/20' : 'bg-white border-gray-200'
                      }`}
                    >
                      {degreeTypes
                        .filter(degree => 
                          !degreeTypeSearch || 
                          degree.toLowerCase().includes(degreeTypeSearch.toLowerCase())
                        )
                        .slice(0, 12)
                        .map(degree => (
                          <button
                            key={degree}
                            onClick={() => {
                              // Clear specialization and update target degree when current degree changes
                              const suggestedTarget = getSuggestedTargetDegree(degree);
                              setProfile({ 
                                ...profile, 
                                current_degree: degree, 
                                field_of_study: '',
                                target_degree: suggestedTarget
                              });
                              setDegreeTypeSearch('');
                              setSpecializationSearch('');
                              setShowDegreeTypeDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                              profile.current_degree === degree
                                ? 'bg-ux-cyan/20 text-ux-cyan'
                                : isDark 
                                  ? 'text-white hover:bg-white/10' 
                                  : 'text-ux-charcoal hover:bg-gray-100'
                            }`}
                          >
                            {degree}
                          </button>
                        ))
                      }
                      {degreeTypeSearch && !degreeTypes.some(d => d.toLowerCase() === degreeTypeSearch.toLowerCase()) && (
                        <button
                          onClick={() => {
                            // Clear specialization and update target degree when current degree changes
                            const suggestedTarget = getSuggestedTargetDegree(degreeTypeSearch);
                            setProfile({ 
                              ...profile, 
                              current_degree: degreeTypeSearch, 
                              field_of_study: '',
                              target_degree: suggestedTarget
                            });
                            setDegreeTypeSearch('');
                            setSpecializationSearch('');
                            setShowDegreeTypeDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm border-t ${
                            isDark ? 'border-white/10 text-ux-cyan' : 'border-gray-100 text-ux-cyan'
                          }`}
                        >
                          + Add "{degreeTypeSearch}" as custom degree
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="relative" ref={specializationRef}>
                  <Label className={isDark ? 'text-white/70' : ''}>Current Course / Specialization</Label>
                  <div className="relative mt-1">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`} />
                    <Input
                      placeholder={profile.current_degree 
                        ? `Search specializations for ${profile.current_degree}...` 
                        : "Select a degree first..."}
                      value={specializationSearch || profile.field_of_study}
                      onChange={(e) => {
                        setSpecializationSearch(e.target.value);
                        setShowSpecializationDropdown(true);
                        // Also update the actual field for custom entries
                        if (e.target.value) {
                          setProfile({ ...profile, field_of_study: e.target.value });
                        }
                      }}
                      onFocus={() => profile.current_degree && setShowSpecializationDropdown(true)}
                      className={`pl-9 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                      data-testid="specialization-input"
                    />
                    {profile.field_of_study && !specializationSearch && (
                      <button
                        onClick={() => {
                          setProfile({ ...profile, field_of_study: '' });
                          setSpecializationSearch('');
                        }}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40 hover:text-white' : 'text-ux-charcoal/40 hover:text-ux-charcoal'}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Specialization Suggestions Dropdown */}
                  {showSpecializationDropdown && profile.current_degree && (
                    <div 
                      className={`absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-lg border shadow-lg ${
                        isDark ? 'bg-[#233842] border-white/20' : 'bg-white border-gray-200'
                      }`}
                    >
                      {/* Show suggestions based on selected degree */}
                      {(() => {
                        const suggestions = degreeSpecializationsMap[profile.current_degree] || degreeSpecializationsMap['Other'];
                        const filteredSuggestions = suggestions.filter(spec =>
                          !specializationSearch || 
                          spec.toLowerCase().includes(specializationSearch.toLowerCase())
                        );
                        
                        if (filteredSuggestions.length === 0 && specializationSearch) {
                          return (
                            <button
                              onClick={() => {
                                setProfile({ ...profile, field_of_study: specializationSearch });
                                setSpecializationSearch('');
                                setShowSpecializationDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm ${
                                isDark ? 'text-ux-cyan' : 'text-ux-cyan'
                              }`}
                            >
                              + Use "{specializationSearch}" as custom specialization
                            </button>
                          );
                        }
                        
                        return (
                          <>
                            {filteredSuggestions.slice(0, 10).map(spec => (
                              <button
                                key={spec}
                                onClick={() => {
                                  setProfile({ ...profile, field_of_study: spec });
                                  setSpecializationSearch('');
                                  setShowSpecializationDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                                  profile.field_of_study === spec
                                    ? 'bg-ux-cyan/20 text-ux-cyan'
                                    : isDark 
                                      ? 'text-white hover:bg-white/10' 
                                      : 'text-ux-charcoal hover:bg-gray-100'
                                }`}
                              >
                                {spec}
                              </button>
                            ))}
                            {specializationSearch && !filteredSuggestions.some(s => s.toLowerCase() === specializationSearch.toLowerCase()) && (
                              <button
                                onClick={() => {
                                  setProfile({ ...profile, field_of_study: specializationSearch });
                                  setSpecializationSearch('');
                                  setShowSpecializationDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2.5 text-sm border-t ${
                                  isDark ? 'border-white/10 text-ux-cyan' : 'border-gray-100 text-ux-cyan'
                                }`}
                              >
                                + Use "{specializationSearch}" as custom specialization
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Hint text when no degree selected */}
                  {!profile.current_degree && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                      Select your degree level above to see relevant specializations
                    </p>
                  )}
                </div>
              </div>
              
              {/* Current Qualification Display */}
              {profile.current_degree && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-ux-slate-light/50'}`}>
                  <span className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Current Qualification:</span>
                  <div className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {profile.current_degree}{profile.field_of_study ? ` in ${profile.field_of_study}` : ''}
                  </div>
                </div>
              )}
              
              {/* GPA Section - Mandatory Calculator (in Current Education) */}
              <div className={`p-4 rounded-xl border-2 ${
                profile.gpa 
                  ? isDark 
                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                    : 'bg-emerald-50 border-emerald-200'
                  : isDark 
                    ? 'bg-amber-500/10 border-amber-500/30' 
                    : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calculator className={`w-5 h-5 ${profile.gpa ? 'text-emerald-500' : 'text-amber-500'}`} />
                    <Label className={`font-semibold ${isDark ? 'text-white' : ''}`}>
                      Your GPA Score
                    </Label>
                    {!profile.gpa && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500 text-white font-medium">
                        Required
                      </span>
                    )}
                  </div>
                </div>
                
                {profile.gpa ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-3">
                      <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {profile.gpa}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                        out of {profile.gpa_scale}.0
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Save the entire profile state to restore upon return
                        localStorage.setItem('ai_finder_profile_backup', JSON.stringify(profile));
                        localStorage.setItem('ai_finder_return', JSON.stringify({
                          returnTo: '/uac-desk/ai-finder',
                          currentGpa: profile.gpa,
                          currentScale: profile.gpa_scale
                        }));
                        navigate('/uac-desk/gpa-calculator?source=ai-finder');
                      }}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Recalculate
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className={`text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                      Use our GPA Calculator to convert your scores accurately. This ensures consistent data for better university matching.
                    </p>
                    <Button
                      onClick={() => {
                        // Save the entire profile state to restore upon return
                        localStorage.setItem('ai_finder_profile_backup', JSON.stringify(profile));
                        localStorage.setItem('ai_finder_return', JSON.stringify({
                          returnTo: '/uac-desk/ai-finder',
                          currentGpa: profile.gpa,
                          currentScale: profile.gpa_scale
                        }));
                        navigate('/uac-desk/gpa-calculator?source=ai-finder');
                      }}
                      className="w-full bg-gradient-to-r from-ux-violet to-purple-600 hover:opacity-90 text-white"
                    >
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate My GPA
                    </Button>
                  </div>
                )}
              </div>
              </div>
              
              {/* Target Education Section */}
              <div className={`p-5 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-emerald-50 to-teal-50'} mt-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Target Education</h3>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>What you want to study abroad</p>
                  </div>
                </div>
              
              {/* Study Area - Autocomplete Style Selection */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-white/70' : ''}>
                  Target Study Area <span className="text-red-400">*</span>
                </Label>
                
                <div className="relative" ref={studyAreaRef}>
                  {/* Search Input with Selected Tag */}
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                    isDark ? 'bg-white/5 border-white/20' : 'bg-white border-gray-200'
                  }`}>
                    <Search className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                    
                    {/* Selected Tag */}
                    {profile.current_study_area && !studyAreaSearch && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-ux-cyan text-white">
                        {profile.current_study_area}
                        <button
                          type="button"
                          onClick={() => {
                            setProfile({ ...profile, current_study_area: '', selected_fields: [] });
                          }}
                          className="hover:bg-white/20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    
                    {/* Search Input */}
                    {(!profile.current_study_area || studyAreaSearch) && (
                      <input
                        type="text"
                        placeholder={profile.current_study_area ? "Change study area..." : "Type to search study areas..."}
                        value={studyAreaSearch}
                        onChange={(e) => {
                          setStudyAreaSearch(e.target.value);
                          setShowStudyAreaDropdown(true);
                        }}
                        onFocus={() => setShowStudyAreaDropdown(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && showStudyAreaDropdown && studyAreaSearch) {
                            e.preventDefault();
                            const filtered = studyAreas.filter(area => 
                              area.toLowerCase().includes(studyAreaSearch.trim().toLowerCase())
                            );
                            if (filtered.length > 0) {
                              const recommendedFields = studyAreaMappings[filtered[0]] || [];
                              setProfile({ 
                                ...profile, 
                                current_study_area: filtered[0],
                                selected_fields: recommendedFields 
                              });
                              setStudyAreaSearch('');
                              setShowStudyAreaDropdown(false);
                            }
                          }
                        }}
                        className={`flex-1 bg-transparent outline-none text-sm ${
                          isDark ? 'text-white placeholder-white/40' : 'text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    )}
                    
                    {/* Edit button if already selected */}
                    {profile.current_study_area && !studyAreaSearch && (
                      <button
                        type="button"
                        onClick={() => setStudyAreaSearch(' ')}
                        className={`text-xs ${isDark ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        Change
                      </button>
                    )}
                  </div>
                  
                  {/* Dropdown with filtered chips */}
                  {showStudyAreaDropdown && studyAreaSearch && (
                    <div className={`absolute z-50 w-full mt-2 p-3 rounded-lg border shadow-lg ${
                      isDark ? 'bg-[#233842] border-white/20' : 'bg-white border-gray-200'
                    }`}>
                      <div className="flex flex-wrap gap-2">
                        {studyAreas
                          .filter(area => 
                            area.toLowerCase().includes(studyAreaSearch.trim().toLowerCase())
                          )
                          .slice(0, 15)
                          .map(area => (
                            <button
                              key={area}
                              type="button"
                              onClick={() => {
                                const recommendedFields = studyAreaMappings[area] || [];
                                setProfile({ 
                                  ...profile, 
                                  current_study_area: area,
                                  selected_fields: recommendedFields 
                                });
                                setStudyAreaSearch('');
                                setShowStudyAreaDropdown(false);
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 
                                ${isDark 
                                  ? 'bg-white/10 text-white/80 hover:bg-ux-cyan hover:text-white border border-white/20' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-ux-cyan hover:text-white border border-gray-200'
                                }`}
                            >
                              {area}
                            </button>
                          ))
                        }
                        {studyAreas.filter(area => 
                          area.toLowerCase().includes(studyAreaSearch.trim().toLowerCase())
                        ).length === 0 && (
                          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                            No matching study areas found
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Field of Study - Pre-selected based on Study Area */}
              {profile.current_study_area && (
                <div className="space-y-3">
                  <Label className={isDark ? 'text-white/70' : ''}>
                    Field of Study <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>(recommended fields pre-selected)</span>
                  </Label>
                  
                  {/* Search for additional fields */}
                  <div className="relative">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`} />
                    <Input
                      placeholder="Search to add more fields..."
                      value={fieldSearch || ''}
                      onChange={(e) => setFieldSearch(e.target.value)}
                      className={`pl-9 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                    />
                  </div>
                  
                  {/* Selected Fields (Recommended) */}
                  <div>
                    <p className={`text-xs mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      Recommended fields (click to deselect):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(profile.selected_fields || studyAreaMappings[profile.current_study_area] || []).map(field => (
                        <button
                          key={field}
                          type="button"
                          onClick={() => {
                            const currentFields = profile.selected_fields || studyAreaMappings[profile.current_study_area] || [];
                            setProfile({ 
                              ...profile, 
                              selected_fields: currentFields.filter(f => f !== field) 
                            });
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 
                            bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30
                            flex items-center gap-1`}
                        >
                          {field}
                          <X className="w-3 h-3" />
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Available Fields to Add */}
                  {fieldSearch && (
                    <div>
                      <p className={`text-xs mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        Click to add:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {studyAreas
                          .filter(field => 
                            field.toLowerCase().includes(fieldSearch.toLowerCase()) &&
                            !(profile.selected_fields || studyAreaMappings[profile.current_study_area] || []).includes(field)
                          )
                          .slice(0, 10)
                          .map(field => (
                            <button
                              key={field}
                              type="button"
                              onClick={() => {
                                const currentFields = profile.selected_fields || studyAreaMappings[profile.current_study_area] || [];
                                setProfile({ 
                                  ...profile, 
                                  selected_fields: [...currentFields, field] 
                                });
                                setFieldSearch('');
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 
                                ${isDark 
                                  ? 'bg-white/10 text-white/70 hover:bg-ux-cyan/20 hover:text-ux-cyan border border-white/20' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-ux-cyan/20 hover:text-ux-cyan border border-gray-200'
                                }
                                flex items-center gap-1`}
                            >
                              <Plus className="w-3 h-3" />
                              {field}
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Target Degree Level</Label>
                {(() => {
                  // Determine available target degrees based on current education
                  const getAvailableTargetDegrees = () => {
                    if (!profile.current_degree) {
                      // No current degree selected - show all options
                      return [
                        { value: 'bachelors', label: 'Bachelors' },
                        { value: 'masters', label: 'Masters' },
                        { value: 'phd', label: 'PhD' },
                        { value: 'diploma', label: 'Diploma' }
                      ];
                    }
                    
                    const currentLower = profile.current_degree.toLowerCase();
                    
                    // High School / Pre-University level
                    if (currentLower.includes('12th') || currentLower.includes('high school') || 
                        currentLower.includes('10+2') || currentLower.includes('higher secondary') ||
                        currentLower.includes('a-level') || currentLower.includes('ib diploma') ||
                        currentLower.includes('hsc') || currentLower.includes('intermediate')) {
                      return [
                        { value: 'bachelors', label: 'Bachelors', recommended: true },
                        { value: 'diploma', label: 'Diploma' }
                      ];
                    }
                    
                    // Diploma level
                    if (currentLower.includes('diploma') || currentLower.includes('iti') || 
                        currentLower.includes('polytechnic')) {
                      return [
                        { value: 'bachelors', label: 'Bachelors', recommended: true },
                        { value: 'diploma', label: 'Advanced Diploma' }
                      ];
                    }
                    
                    // Undergraduate level (B.Tech, B.Sc, BA, etc.)
                    if (currentLower.startsWith('b.') || currentLower.startsWith('ba') || 
                        currentLower.startsWith('bsc') || currentLower.startsWith('btech') ||
                        currentLower.startsWith('be ') || currentLower.includes('bachelor') ||
                        currentLower === 'bba' || currentLower === 'bca' || currentLower === 'bcom' ||
                        currentLower === 'mbbs' || currentLower === 'bds' || currentLower === 'llb') {
                      return [
                        { value: 'masters', label: 'Masters', recommended: true },
                        { value: 'phd', label: 'PhD' }
                      ];
                    }
                    
                    // Postgraduate level (M.Tech, MBA, MSc, etc.)
                    if (currentLower.startsWith('m.') || currentLower.startsWith('ma') || 
                        currentLower.startsWith('msc') || currentLower.startsWith('mtech') ||
                        currentLower.startsWith('me ') || currentLower.includes('master') ||
                        currentLower === 'mba' || currentLower === 'mca' || currentLower === 'mcom' ||
                        currentLower === 'md' || currentLower === 'ms' || currentLower === 'llm' ||
                        currentLower.includes('pgdm') || currentLower.includes('pg diploma')) {
                      return [
                        { value: 'phd', label: 'PhD', recommended: true },
                        { value: 'masters', label: 'Masters (different specialization)' }
                      ];
                    }
                    
                    // Default - show all
                    return [
                      { value: 'bachelors', label: 'Bachelors' },
                      { value: 'masters', label: 'Masters' },
                      { value: 'phd', label: 'PhD' },
                      { value: 'diploma', label: 'Diploma' }
                    ];
                  };
                  
                  const availableDegrees = getAvailableTargetDegrees();
                  
                  return (
                    <Select value={profile.target_degree} onValueChange={(v) => setProfile({ ...profile, target_degree: v })}>
                      <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDegrees.map(d => (
                          <SelectItem key={d.value} value={d.value}>
                            <span className="flex items-center gap-2">
                              {d.label}
                              {d.recommended && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-500">
                                  Recommended
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              </div>
              
              {/* Work Experience - Separate Section */}
              <div className={`p-5 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-gray-50 to-slate-50'} mt-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Work Experience</h3>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Professional experience (if any)</p>
                  </div>
                </div>
                
                <div>
                  <Label className={isDark ? 'text-white/70' : ''}>Years of Experience</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={profile.work_experience_years}
                    onChange={(e) => setProfile({ ...profile, work_experience_years: parseInt(e.target.value) || 0 })}
                    className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                    Enter 0 if you're a fresh graduate
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Test Scores */}
          {currentStep === 2 && (
            <div className="space-y-8">
              {/* Hero Section */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/10 to-indigo-500/10 mb-4">
                  <FileText className="w-5 h-5 text-violet-500" />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Step 2 of 5
                  </span>
                </div>
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  Got any test scores? 📝
                </h2>
                <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  Optional - skip if you haven't taken them yet!
                </p>
              </div>
              
              {/* Admission Tests */}
              <div className={`p-5 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-violet-50 to-indigo-50'}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                    <GraduationCap className="w-4 h-4 text-white" />
                  </div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Admission Tests
                  </h3>
                  <span className={`text-xs ml-auto ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                    GRE/GMAT for Masters • SAT for Bachelors
                  </span>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* GRE */}
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Label className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>GRE Score</Label>
                      <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>260 - 340</span>
                    </div>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter your score"
                        value={profile.gre_score}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 340)) {
                            setProfile({ ...profile, gre_score: val });
                          }
                        }}
                        className={`text-lg font-semibold pr-10 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                      />
                      {profile.gre_score && (
                        <button
                          type="button"
                          onClick={() => setProfile({ ...profile, gre_score: '' })}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 ${isDark ? 'hover:bg-white/10 text-white/50' : 'text-gray-400'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {profile.gre_score && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isDark ? 'text-white/40' : 'text-ux-charcoal/40'}>Score Percentile</span>
                          <span className={`font-medium ${
                            parseInt(profile.gre_score) >= 320 ? 'text-emerald-500' :
                            parseInt(profile.gre_score) >= 300 ? 'text-blue-500' : 'text-amber-500'
                          }`}>
                            {parseInt(profile.gre_score) >= 330 ? 'Excellent (90%+)' :
                             parseInt(profile.gre_score) >= 320 ? 'Very Good (80%+)' :
                             parseInt(profile.gre_score) >= 310 ? 'Good (65%+)' :
                             parseInt(profile.gre_score) >= 300 ? 'Average (50%+)' : 'Below Average'}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              parseInt(profile.gre_score) >= 320 ? 'bg-emerald-500' :
                              parseInt(profile.gre_score) >= 300 ? 'bg-blue-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${Math.min(100, Math.max(0, ((parseInt(profile.gre_score) - 260) / 80) * 100))}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* GMAT */}
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Label className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>GMAT Score</Label>
                      <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>200 - 800</span>
                    </div>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter your score"
                        value={profile.gmat_score}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 800)) {
                            setProfile({ ...profile, gmat_score: val });
                          }
                        }}
                        className={`text-lg font-semibold pr-10 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                      />
                      {profile.gmat_score && (
                        <button
                          type="button"
                          onClick={() => setProfile({ ...profile, gmat_score: '' })}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 ${isDark ? 'hover:bg-white/10 text-white/50' : 'text-gray-400'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {profile.gmat_score && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isDark ? 'text-white/40' : 'text-ux-charcoal/40'}>Score Percentile</span>
                          <span className={`font-medium ${
                            parseInt(profile.gmat_score) >= 700 ? 'text-emerald-500' :
                            parseInt(profile.gmat_score) >= 600 ? 'text-blue-500' : 'text-amber-500'
                          }`}>
                            {parseInt(profile.gmat_score) >= 730 ? 'Excellent (96%+)' :
                             parseInt(profile.gmat_score) >= 700 ? 'Very Good (88%+)' :
                             parseInt(profile.gmat_score) >= 650 ? 'Good (73%+)' :
                             parseInt(profile.gmat_score) >= 600 ? 'Average (56%+)' : 'Below Average'}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              parseInt(profile.gmat_score) >= 700 ? 'bg-emerald-500' :
                              parseInt(profile.gmat_score) >= 600 ? 'bg-blue-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${Math.min(100, Math.max(0, ((parseInt(profile.gmat_score) - 200) / 600) * 100))}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* SAT */}
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Label className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>SAT Score</Label>
                      <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>400 - 1600</span>
                    </div>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter your score"
                        value={profile.sat_score}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 1600)) {
                            setProfile({ ...profile, sat_score: val });
                          }
                        }}
                        className={`text-lg font-semibold pr-10 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                      />
                      {profile.sat_score && (
                        <button
                          type="button"
                          onClick={() => setProfile({ ...profile, sat_score: '' })}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 ${isDark ? 'hover:bg-white/10 text-white/50' : 'text-gray-400'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {profile.sat_score && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isDark ? 'text-white/40' : 'text-ux-charcoal/40'}>Score Percentile</span>
                          <span className={`font-medium ${
                            parseInt(profile.sat_score) >= 1400 ? 'text-emerald-500' :
                            parseInt(profile.sat_score) >= 1200 ? 'text-blue-500' : 'text-amber-500'
                          }`}>
                            {parseInt(profile.sat_score) >= 1500 ? 'Excellent (99%+)' :
                             parseInt(profile.sat_score) >= 1400 ? 'Very Good (94%+)' :
                             parseInt(profile.sat_score) >= 1300 ? 'Good (87%+)' :
                             parseInt(profile.sat_score) >= 1200 ? 'Above Average (74%+)' :
                             parseInt(profile.sat_score) >= 1000 ? 'Average (40%+)' : 'Below Average'}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              parseInt(profile.sat_score) >= 1400 ? 'bg-emerald-500' :
                              parseInt(profile.sat_score) >= 1200 ? 'bg-blue-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${Math.min(100, Math.max(0, ((parseInt(profile.sat_score) - 400) / 1200) * 100))}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {/* Quick select scores */}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {[1200, 1300, 1400, 1450, 1500, 1550].map(score => (
                        <button
                          key={score}
                          type="button"
                          onClick={() => setProfile({ ...profile, sat_score: score.toString() })}
                          className={`px-2.5 py-1 text-xs rounded-full transition-all ${
                            profile.sat_score === score.toString()
                              ? 'bg-violet-500 text-white'
                              : isDark 
                                ? 'bg-white/10 text-white/70 hover:bg-white/20' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* English Proficiency Tests */}
              <div className={`p-5 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-cyan-50 to-teal-50'}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                    <Globe className="w-4 h-4 text-white" />
                  </div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    English Proficiency Tests
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* IELTS */}
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Label className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>IELTS Score</Label>
                      <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>0 - 9</span>
                    </div>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="Enter your band score"
                        value={profile.ielts_score}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 9)) {
                            setProfile({ ...profile, ielts_score: val });
                          }
                        }}
                        className={`text-lg font-semibold pr-10 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                      />
                      {profile.ielts_score && (
                        <button
                          type="button"
                          onClick={() => setProfile({ ...profile, ielts_score: '' })}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 ${isDark ? 'hover:bg-white/10 text-white/50' : 'text-gray-400'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {profile.ielts_score && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isDark ? 'text-white/40' : 'text-ux-charcoal/40'}>Proficiency Level</span>
                          <span className={`font-medium ${
                            parseFloat(profile.ielts_score) >= 7.5 ? 'text-emerald-500' :
                            parseFloat(profile.ielts_score) >= 6.5 ? 'text-blue-500' : 'text-amber-500'
                          }`}>
                            {parseFloat(profile.ielts_score) >= 8 ? 'Expert User' :
                             parseFloat(profile.ielts_score) >= 7 ? 'Very Good User' :
                             parseFloat(profile.ielts_score) >= 6 ? 'Competent User' :
                             parseFloat(profile.ielts_score) >= 5 ? 'Modest User' : 'Limited User'}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              parseFloat(profile.ielts_score) >= 7.5 ? 'bg-emerald-500' :
                              parseFloat(profile.ielts_score) >= 6.5 ? 'bg-blue-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${Math.min(100, (parseFloat(profile.ielts_score) / 9) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {/* Quick select bands */}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {[5.5, 6, 6.5, 7, 7.5, 8, 8.5].map(band => (
                        <button
                          key={band}
                          type="button"
                          onClick={() => setProfile({ ...profile, ielts_score: band.toString() })}
                          className={`px-2.5 py-1 text-xs rounded-full transition-all ${
                            profile.ielts_score === band.toString()
                              ? 'bg-cyan-500 text-white'
                              : isDark 
                                ? 'bg-white/10 text-white/70 hover:bg-white/20' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {band}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* TOEFL */}
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Label className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>TOEFL Score</Label>
                      <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>0 - 120</span>
                    </div>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter your score"
                        value={profile.toefl_score}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 120)) {
                            setProfile({ ...profile, toefl_score: val });
                          }
                        }}
                        className={`text-lg font-semibold pr-10 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                      />
                      {profile.toefl_score && (
                        <button
                          type="button"
                          onClick={() => setProfile({ ...profile, toefl_score: '' })}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 ${isDark ? 'hover:bg-white/10 text-white/50' : 'text-gray-400'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {profile.toefl_score && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isDark ? 'text-white/40' : 'text-ux-charcoal/40'}>Proficiency Level</span>
                          <span className={`font-medium ${
                            parseInt(profile.toefl_score) >= 100 ? 'text-emerald-500' :
                            parseInt(profile.toefl_score) >= 80 ? 'text-blue-500' : 'text-amber-500'
                          }`}>
                            {parseInt(profile.toefl_score) >= 110 ? 'Expert (C2)' :
                             parseInt(profile.toefl_score) >= 95 ? 'Advanced (C1)' :
                             parseInt(profile.toefl_score) >= 72 ? 'Upper-Intermediate (B2)' :
                             parseInt(profile.toefl_score) >= 42 ? 'Intermediate (B1)' : 'Basic'}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              parseInt(profile.toefl_score) >= 100 ? 'bg-emerald-500' :
                              parseInt(profile.toefl_score) >= 80 ? 'bg-blue-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${Math.min(100, (parseInt(profile.toefl_score) / 120) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {/* Quick select scores */}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {[80, 90, 100, 105, 110, 115].map(score => (
                        <button
                          key={score}
                          type="button"
                          onClick={() => setProfile({ ...profile, toefl_score: score.toString() })}
                          className={`px-2.5 py-1 text-xs rounded-full transition-all ${
                            profile.toefl_score === score.toString()
                              ? 'bg-teal-500 text-white'
                              : isDark 
                                ? 'bg-white/10 text-white/70 hover:bg-white/20' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* PTE */}
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Label className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>PTE Score</Label>
                      <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>10 - 90</span>
                    </div>
                    <div className="relative">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter your score"
                        value={profile.pte_score}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 90)) {
                            setProfile({ ...profile, pte_score: val });
                          }
                        }}
                        className={`text-lg font-semibold pr-10 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                      />
                      {profile.pte_score && (
                        <button
                          type="button"
                          onClick={() => setProfile({ ...profile, pte_score: '' })}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 ${isDark ? 'hover:bg-white/10 text-white/50' : 'text-gray-400'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {profile.pte_score && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isDark ? 'text-white/40' : 'text-ux-charcoal/40'}>Proficiency Level</span>
                          <span className={`font-medium ${
                            parseInt(profile.pte_score) >= 76 ? 'text-emerald-500' :
                            parseInt(profile.pte_score) >= 59 ? 'text-blue-500' : 'text-amber-500'
                          }`}>
                            {parseInt(profile.pte_score) >= 85 ? 'Expert (C2)' :
                             parseInt(profile.pte_score) >= 76 ? 'Advanced (C1)' :
                             parseInt(profile.pte_score) >= 59 ? 'Upper-Intermediate (B2)' :
                             parseInt(profile.pte_score) >= 43 ? 'Intermediate (B1)' : 'Basic'}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              parseInt(profile.pte_score) >= 76 ? 'bg-emerald-500' :
                              parseInt(profile.pte_score) >= 59 ? 'bg-blue-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${Math.min(100, ((parseInt(profile.pte_score) - 10) / 80) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {/* Quick select scores */}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {[50, 58, 65, 72, 79, 83].map(score => (
                        <button
                          key={score}
                          type="button"
                          onClick={() => setProfile({ ...profile, pte_score: score.toString() })}
                          className={`px-2.5 py-1 text-xs rounded-full transition-all ${
                            profile.pte_score === score.toString()
                              ? 'bg-teal-500 text-white'
                              : isDark 
                                ? 'bg-white/10 text-white/70 hover:bg-white/20' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Haven't taken tests info */}
              <div className={`p-4 rounded-xl border-2 border-dashed ${isDark ? 'border-white/20 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-ux-charcoal'}`}>
                      Haven't taken these tests yet?
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                      No problem! We'll recommend universities based on your academic profile and suggest what scores you might need.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Preferences */}
          {currentStep === 3 && (
            <div className="space-y-8">
              {/* Hero Section */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-violet-500/10 mb-4">
                  <Globe className="w-5 h-5 text-cyan-500" />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Step 3 of 5
                  </span>
                </div>
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  Where's your dream destination? 🌍
                </h2>
                <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  Pick the countries you'd love to explore
                </p>
              </div>
              
              {/* Country Selection - Card Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {[
                  { name: 'Australia', flag: '🇦🇺', color: 'from-blue-600 to-yellow-500' },
                  { name: 'Canada', flag: '🇨🇦', color: 'from-red-500 to-white' },
                  { name: 'Dubai', flag: '🇦🇪', color: 'from-green-600 to-red-600' },
                  { name: 'France', flag: '🇫🇷', color: 'from-blue-600 to-red-500' },
                  { name: 'Germany', flag: '🇩🇪', color: 'from-black to-yellow-500' },
                  { name: 'Ireland', flag: '🇮🇪', color: 'from-green-500 to-orange-500' },
                  { name: 'Netherlands', flag: '🇳🇱', color: 'from-red-500 to-blue-600' },
                  { name: 'New Zealand', flag: '🇳🇿', color: 'from-blue-700 to-red-600' },
                  { name: 'UK', flag: '🇬🇧', color: 'from-red-500 to-blue-700' },
                  { name: 'USA', flag: '🇺🇸', color: 'from-blue-500 to-red-500' },
                ].map(({ name, flag, color }) => {
                  const isSelected = profile.preferred_countries.includes(name);
                  return (
                    <button
                      key={name}
                      onClick={() => handleCountryToggle(name)}
                      className={`relative p-5 rounded-2xl transition-all duration-300 transform hover:scale-105 text-center ${
                        isSelected
                          ? isDark 
                            ? 'bg-gradient-to-br from-cyan-500/30 to-violet-500/30 border-2 border-cyan-400 shadow-lg shadow-cyan-500/20'
                            : 'bg-gradient-to-br from-cyan-50 to-violet-50 border-2 border-cyan-400 shadow-lg shadow-cyan-200'
                          : isDark 
                            ? 'bg-white/5 hover:bg-white/10 border border-white/10' 
                            : 'bg-white hover:shadow-md border border-gray-200'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-cyan-400 to-cyan-500 rounded-full flex items-center justify-center shadow-md">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className="text-4xl mb-2">{flag}</div>
                      <div className={`text-sm font-semibold ${
                        isSelected 
                          ? 'text-cyan-500' 
                          : isDark ? 'text-white/80' : 'text-ux-charcoal'
                      }`}>
                        {name}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {/* Selected Countries Display - Inline badges */}
              {profile.preferred_countries.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 justify-center pt-6 mt-4 border-t border-white/10">
                  <span className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    Selected:
                  </span>
                  {profile.preferred_countries.map(country => {
                    const countryData = [
                      { name: 'Australia', flag: '🇦🇺' },
                      { name: 'Canada', flag: '🇨🇦' },
                      { name: 'Dubai', flag: '🇦🇪' },
                      { name: 'France', flag: '🇫🇷' },
                      { name: 'Germany', flag: '🇩🇪' },
                      { name: 'Ireland', flag: '🇮🇪' },
                      { name: 'Netherlands', flag: '🇳🇱' },
                      { name: 'New Zealand', flag: '🇳🇿' },
                      { name: 'UK', flag: '🇬🇧' },
                      { name: 'USA', flag: '🇺🇸' },
                    ].find(c => c.name === country);
                    return (
                      <span 
                        key={country} 
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                          isDark 
                            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' 
                            : 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                        }`}
                      >
                        <span className="text-base">{countryData?.flag}</span>
                        {country}
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleCountryToggle(country); }}
                          className="hover:bg-white/20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* GPA Conversion Reassurance Message */}
              {profile.original_percentage && profile.preferred_countries.length > 0 && (
                <div className={`p-4 rounded-xl border ${
                  isDark 
                    ? 'bg-emerald-500/10 border-emerald-500/20' 
                    : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        Your GPA will be automatically converted
                      </p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                        Based on your score of <span className="font-semibold">{profile.original_percentage}%</span>, 
                        we'll convert your GPA to each country's scale (e.g., 4.0 for USA, 7.0 for Australia, Honours for UK) 
                        to ensure accurate university matching.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Quick Info Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">💡</span>
                    <span className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Pro Tip</span>
                  </div>
                  <p className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    Select 2-3 countries to get diverse options that match your profile
                  </p>
                </div>
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-emerald-50 to-teal-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">🎯</span>
                    <span className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Based on Your Profile</span>
                  </div>
                  <p className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    {profile.current_study_area ? `Great options for ${profile.current_study_area} students!` : 'Complete Step 1 for personalized suggestions'}
                  </p>
                </div>
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-amber-50 to-orange-50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">✨</span>
                    <span className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Your Study Area</span>
                  </div>
                  <p className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    {profile.current_study_area || 'Not selected yet'} {profile.selected_fields?.length > 0 && `• ${profile.selected_fields.slice(0,2).join(', ')}`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Budget & Goals */}
          {currentStep === 4 && (
            <div className="space-y-8">
              {/* Hero Section */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-teal-500/10 mb-4">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Step 4 of 5
                  </span>
                </div>
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  Let's talk budget 💰
                </h2>
                <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  Help us find programs that fit your finances
                </p>
              </div>
              
              {/* Budget Section */}
              <div className={`p-6 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-emerald-50 to-teal-50'}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                      <Banknote className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Annual Budget</h3>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Tuition + Living expenses</p>
                    </div>
                  </div>
                  
                  {/* Currency Selector */}
                  <div className="flex items-center gap-2">
                    {(() => {
                      // Currency options based on selected countries
                      const currencyMap = {
                        'USA': { code: 'USD', symbol: '$', flag: '🇺🇸' },
                        'UK': { code: 'GBP', symbol: '£', flag: '🇬🇧' },
                        'Canada': { code: 'CAD', symbol: 'C$', flag: '🇨🇦' },
                        'Australia': { code: 'AUD', symbol: 'A$', flag: '🇦🇺' },
                        'Germany': { code: 'EUR', symbol: '€', flag: '🇩🇪' },
                        'France': { code: 'EUR', symbol: '€', flag: '🇫🇷' },
                        'Netherlands': { code: 'EUR', symbol: '€', flag: '🇳🇱' },
                        'Ireland': { code: 'EUR', symbol: '€', flag: '🇮🇪' },
                        'New Zealand': { code: 'NZD', symbol: 'NZ$', flag: '🇳🇿' },
                        'Dubai': { code: 'AED', symbol: 'د.إ', flag: '🇦🇪' },
                      };
                      
                      // Get unique currencies from selected countries + INR
                      const selectedCurrencies = profile.preferred_countries
                        .map(c => currencyMap[c])
                        .filter(Boolean)
                        .filter((v, i, a) => a.findIndex(x => x.code === v.code) === i);
                      
                      // Always include USD and INR
                      const availableCurrencies = [
                        { code: 'USD', symbol: '$', flag: '🇺🇸' },
                        { code: 'INR', symbol: '₹', flag: '🇮🇳' },
                        ...selectedCurrencies.filter(c => c.code !== 'USD')
                      ].filter((v, i, a) => a.findIndex(x => x.code === v.code) === i);
                      
                      return (
                        <div className="flex rounded-xl overflow-hidden border border-gray-200">
                          {availableCurrencies.slice(0, 4).map(curr => (
                            <button
                              key={curr.code}
                              onClick={() => setBudgetCurrency(curr.code)}
                              className={`px-3 py-2 text-xs font-medium transition-all ${
                                budgetCurrency === curr.code
                                  ? 'bg-emerald-500 text-white'
                                  : isDark 
                                    ? 'bg-white/5 text-white/70 hover:bg-white/10' 
                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {curr.flag} {curr.code}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Budget Display */}
                <div className={`text-center mb-6 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                  <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {(() => {
                      const rates = { USD: 1, INR: 83, GBP: 0.79, EUR: 0.92, CAD: 1.36, AUD: 1.53, NZD: 1.64 };
                      const symbols = { USD: '$', INR: '₹', GBP: '£', EUR: '€', CAD: 'C$', AUD: 'A$', NZD: 'NZ$' };
                      const rate = rates[budgetCurrency] || 1;
                      const symbol = symbols[budgetCurrency] || '$';
                      const min = Math.round(profile.budget_min * rate);
                      const max = Math.round(profile.budget_max * rate);
                      return `${symbol}${min.toLocaleString()} - ${symbol}${max.toLocaleString()}`;
                    })()}
                  </div>
                  <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>per year</p>
                </div>
                
                {/* Slider */}
                <div className="px-2">
                  <Slider
                    value={[profile.budget_min, profile.budget_max]}
                    min={10000}
                    max={100000}
                    step={5000}
                    onValueChange={([min, max]) => setProfile({ ...profile, budget_min: min, budget_max: max })}
                    className="my-4"
                  />
                  <div className={`flex justify-between text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                    <span>$10K</span>
                    <span>$25K</span>
                    <span>$50K</span>
                    <span>$75K</span>
                    <span>$100K</span>
                  </div>
                </div>
                
                {/* Quick Budget Presets */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {[
                    { label: 'Budget-Friendly', min: 10000, max: 25000, emoji: '💡' },
                    { label: 'Mid-Range', min: 25000, max: 50000, emoji: '⚖️' },
                    { label: 'Premium', min: 50000, max: 80000, emoji: '✨' },
                    { label: 'No Limit', min: 10000, max: 100000, emoji: '🚀' },
                  ].map(preset => (
                    <button
                      key={preset.label}
                      onClick={() => setProfile({ ...profile, budget_min: preset.min, budget_max: preset.max })}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        profile.budget_min === preset.min && profile.budget_max === preset.max
                          ? 'bg-emerald-500 text-white'
                          : isDark 
                            ? 'bg-white/10 text-white/70 hover:bg-white/20' 
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {preset.emoji} {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Scholarship Section */}
              <div 
                onClick={() => setProfile({ ...profile, needs_scholarship: !profile.needs_scholarship })}
                className={`p-5 rounded-2xl cursor-pointer transition-all ${
                  profile.needs_scholarship
                    ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white'
                    : isDark 
                      ? 'bg-white/5 hover:bg-white/10' 
                      : 'bg-white hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    profile.needs_scholarship ? 'bg-white/20' : 'bg-violet-100'
                  }`}>
                    <span className="text-2xl">🎓</span>
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${profile.needs_scholarship ? 'text-white' : isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      I need scholarship / financial aid
                    </h3>
                    <p className={`text-sm ${profile.needs_scholarship ? 'text-white/80' : isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                      We'll prioritize universities with funding opportunities
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    profile.needs_scholarship 
                      ? 'bg-white border-white' 
                      : 'border-gray-300'
                  }`}>
                    {profile.needs_scholarship && <Check className="w-4 h-4 text-violet-500" />}
                  </div>
                </div>
              </div>
              
              {/* Career Goals Section */}
              <div className={`p-6 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-amber-50 to-orange-50'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Career Goals</h3>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Optional - helps us find the right fit</p>
                  </div>
                </div>
                
                <Textarea
                  placeholder="What are your career aspirations? (e.g., 'I want to work at a top tech company as a software engineer' or 'I'm interested in research and academia')"
                  value={profile.career_goals}
                  onChange={(e) => setProfile({ ...profile, career_goals: e.target.value })}
                  className={`min-h-[100px] resize-none ${isDark ? 'bg-white/5 border-white/20 text-white placeholder:text-white/30' : 'bg-white border-gray-200'}`}
                />
                
                {/* Quick Career Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {['🏢 Corporate Job', '🔬 Research/Academia', '🚀 Startup', '💼 Consulting', '🌍 International Career', '👨‍💻 Tech Industry'].map(tag => (
                    <button
                      key={tag}
                      onClick={() => setProfile({ 
                        ...profile, 
                        career_goals: profile.career_goals ? `${profile.career_goals}, ${tag.split(' ').slice(1).join(' ')}` : tag.split(' ').slice(1).join(' ')
                      })}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isDark 
                          ? 'bg-white/10 text-white/70 hover:bg-white/20' 
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Student Motivators */}
          {currentStep === 5 && (
            <div className="space-y-6">
              {/* Hero Section */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-rose-500/10 to-pink-500/10 mb-4">
                  <Target className="w-5 h-5 text-rose-500" />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Step 5 of 5 - Final Step! 🎉
                  </span>
                </div>
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  What matters most to you? 🎯
                </h2>
                <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  Almost there! Help us understand your priorities
                </p>
              </div>
              
              {/* Scale Legend */}
              <div className={`flex justify-between text-xs px-2 py-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <span className="text-gray-400">😐 Not Important</span>
                <span className="text-gray-500">🤔 Somewhat</span>
                <span className="text-blue-500">👍 Important</span>
                <span className="text-emerald-500">💪 Very Important</span>
                <span className="text-rose-500">🔥 Critical</span>
              </div>
              
              <div className="space-y-4">
                {[
                  { key: 'priority_course', label: 'Course Quality', description: 'Program curriculum, faculty expertise, research opportunities', icon: BookOpen, color: 'blue', gradient: 'from-blue-500 to-indigo-500', bgActive: 'bg-blue-500', bgIcon: 'bg-blue-500/10', textIcon: 'text-blue-500' },
                  { key: 'priority_ranking', label: 'University Reputation', description: 'Global rankings, brand recognition, prestige', icon: Trophy, color: 'amber', gradient: 'from-amber-500 to-orange-500', bgActive: 'bg-amber-500', bgIcon: 'bg-amber-500/10', textIcon: 'text-amber-500' },
                  { key: 'priority_career', label: 'Career Prospects', description: 'Job placement rates, industry connections, internships', icon: TrendingUp, color: 'emerald', gradient: 'from-emerald-500 to-teal-500', bgActive: 'bg-emerald-500', bgIcon: 'bg-emerald-500/10', textIcon: 'text-emerald-500' },
                  { key: 'priority_pr', label: 'PR / Immigration Pathways', description: 'Post-study work visa, permanent residency options', icon: Plane, color: 'indigo', gradient: 'from-indigo-500 to-purple-500', bgActive: 'bg-indigo-500', bgIcon: 'bg-indigo-500/10', textIcon: 'text-indigo-500' },
                  { key: 'priority_cost', label: 'Affordability', description: 'Tuition fees, living costs, scholarship availability', icon: PiggyBank, color: 'violet', gradient: 'from-violet-500 to-purple-500', bgActive: 'bg-violet-500', bgIcon: 'bg-violet-500/10', textIcon: 'text-violet-500' },
                  { key: 'priority_location', label: 'Location', description: 'City size, climate, safety, proximity to home', icon: MapPin, color: 'rose', gradient: 'from-rose-500 to-pink-500', bgActive: 'bg-rose-500', bgIcon: 'bg-rose-500/10', textIcon: 'text-rose-500' },
                  { key: 'priority_culture', label: 'Cultural Experience', description: 'Campus diversity, student life, extracurricular activities', icon: Globe, color: 'cyan', gradient: 'from-cyan-500 to-teal-500', bgActive: 'bg-cyan-500', bgIcon: 'bg-cyan-500/10', textIcon: 'text-cyan-500' }
                ].map(({ key, label, description, icon: Icon, gradient, bgActive, bgIcon, textIcon }) => {
                  const scaleLabels = ['😐', '🤔', '👍', '💪', '🔥'];
                  const scaleTexts = ['Not Important', 'Somewhat', 'Important', 'Very Important', 'Critical'];
                  const value = profile[key] || 3;
                  
                  return (
                    <div key={key} className={`p-4 rounded-2xl transition-all ${
                      value >= 4 
                        ? `bg-gradient-to-r ${gradient} bg-opacity-10` 
                        : isDark ? 'bg-white/5' : 'bg-white border border-gray-100'
                    }`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          value >= 4 ? 'bg-white/20' : bgIcon
                        }`}>
                          <Icon className={`w-5 h-5 ${value >= 4 ? 'text-white' : textIcon}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-semibold ${value >= 4 ? 'text-white' : isDark ? 'text-white' : 'text-ux-charcoal'}`}>{label}</span>
                            <span className="text-lg">{scaleLabels[value - 1]}</span>
                          </div>
                          <p className={`text-xs ${value >= 4 ? 'text-white/70' : isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                            {description}
                          </p>
                        </div>
                      </div>
                      
                      {/* Interactive Buttons instead of slider */}
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((v) => (
                          <button
                            key={v}
                            onClick={() => setProfile({ ...profile, [key]: v })}
                            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                              value === v
                                ? value >= 4 ? 'bg-white text-gray-800' : `${bgActive} text-white`
                                : value >= 4 
                                  ? 'bg-white/10 text-white/70 hover:bg-white/20' 
                                  : isDark 
                                    ? 'bg-white/5 text-white/50 hover:bg-white/10' 
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {scaleTexts[v - 1]}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Encouraging Message */}
              <div className={`p-4 rounded-2xl text-center ${isDark ? 'bg-gradient-to-r from-cyan-500/10 to-violet-500/10' : 'bg-gradient-to-r from-cyan-50 to-violet-50'}`}>
                <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  🚀 You're all set!
                </p>
                <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  Click "Find Universities" to discover your perfect matches
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-white/10">
            <Button
              variant="outline"
              onClick={() => { setCurrentStep(s => s - 1); scrollToTop(); }}
              disabled={currentStep === 1}
              className={`w-full sm:w-auto ${isDark ? 'border-white/20 hover:bg-white/5' : ''} ${currentStep === 1 ? 'opacity-50' : ''}`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            {/* Progress Dots */}
            <div className="flex items-center gap-2 order-first sm:order-none">
              {[1, 2, 3, 4, 5].map((step) => (
                <button
                  key={step}
                  onClick={() => { setCurrentStep(step); scrollToTop(); }}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    step === currentStep 
                      ? 'w-8 bg-gradient-to-r from-ux-cyan to-ux-violet' 
                      : step < currentStep 
                        ? 'bg-ux-cyan' 
                        : isDark ? 'bg-white/20' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>

            {currentStep < 5 ? (
              <Button
                onClick={() => { setCurrentStep(s => s + 1); scrollToTop(); }}
                className="w-full sm:w-auto bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 px-6"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full sm:w-auto bg-gradient-to-r from-ux-violet to-ux-rose hover:opacity-90 px-4 sm:px-6"
                data-testid="find-universities-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Finding...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Find My Universities</span>
                    <span className="sm:hidden">Find Universities</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
      
      {/* Search Limit Reached Modal */}
      {showLimitReachedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-rose-500 to-red-500">
              <h3 className="font-semibold text-lg text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Search Limit Reached
              </h3>
            </div>
            
            <div className="p-4">
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-rose-500" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  You've used all 5 searches
                </h4>
                <p className="text-sm text-gray-600">
                  To continue exploring universities, please speak with one of our expert counselors who can provide personalized guidance.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm font-medium text-blue-800 mb-2">Why connect with a counselor?</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Get expert advice tailored to your profile</li>
                  <li>• Access to exclusive university partnerships</li>
                  <li>• Application support and document review</li>
                </ul>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowLimitReachedModal(false);
                  navigate('/uac-desk/dashboard');
                }}
              >
                Go to Dashboard
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-ux-green to-emerald-500 text-white"
                onClick={() => {
                  window.open('https://wa.me/919999999999?text=Hi! I\'ve completed my AI searches on the UniXperts portal and would like to speak with a counselor.', '_blank');
                }}
              >
                <Phone className="w-4 h-4 mr-2" />
                Contact Counselor
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIUniversityFinder;
